import React, { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useAuthStore } from '../../store/authStore';
import { useStoreContextStore } from '../../store/storeContextStore';
import { complianceTasksAPI, productsAPI, usersAPI } from '../../services/api';
import {
  mergeComplianceLogsByLatestSubtask,
  summarizeDailyCompliance,
} from '../../utils/complianceSummary';
import {
  normalizeAssignmentBundle,
  buildExpectedSubtaskMap,
  applyComplianceSummaryToTasks,
  orderAssignmentBundleForDisplay,
} from '../../utils/complianceAssignments';
import {
  normalizeTemperatureComplianceSubtaskId,
  parseTemperatureComplianceReading,
} from '../../constants/complianceTemperatureZones';
import { downloadExcelWorkbook } from '../../utils/exportExcel';
import { 
  Calendar, 
  CheckCircle2, 
  Circle, 
  Filter,
  Package, 
  Thermometer, 
  ClipboardCheck,
  AlertCircle,
  TrendingUp,
  User,
  Store,
  XCircle
} from 'lucide-react';
import ExportButton from '../../components/common/ExportButton';
import FilterSelect from '../../components/common/FilterSelect';
import { DataLoadingOverlay } from '../../components/common/LoadingState';

// Calendar Date Picker Component
const DatePicker = ({ value, onChange, disabled }) => {
  const [showCalendar, setShowCalendar] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const calendarRef = useRef(null);
  
  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (calendarRef.current && !calendarRef.current.contains(e.target)) {
        setShowCalendar(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Parse value to Date
  const selectedDate = value ? new Date(value) : null;
  
  // Generate calendar days
  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };
  
  const getFirstDayOfMonth = (year, month) => {
    return new Date(year, month, 1).getDay();
  };
  
  const handleDateSelect = (day) => {
    const year = currentMonth.getFullYear();
    const month = String(currentMonth.getMonth() + 1).padStart(2, '0');
    const dayStr = String(day).padStart(2, '0');
    onChange(`${year}-${month}-${dayStr}`);
    setShowCalendar(false);
  };
  
  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };
  
  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };
  
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const monthOptions = [
    { value: 0, label: 'January' },
    { value: 1, label: 'February' },
    { value: 2, label: 'March' },
    { value: 3, label: 'April' },
    { value: 4, label: 'May' },
    { value: 5, label: 'June' },
    { value: 6, label: 'July' },
    { value: 7, label: 'August' },
    { value: 8, label: 'September' },
    { value: 9, label: 'October' },
    { value: 10, label: 'November' },
    { value: 11, label: 'December' },
  ];
  const yearOptions = Array.from({ length: 21 }, (_, i) => year - 10 + i).map((yearValue) => ({
    value: yearValue,
    label: String(yearValue),
  }));
  
  // Generate days array
  const days = [];
  for (let i = 0; i < firstDay; i++) {
    days.push(null); // Empty cells
  }
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(i);
  }
  
  const isSelected = (day) => {
    if (!selectedDate || !day) return false;
    return selectedDate.getDate() === day && 
           selectedDate.getMonth() === month && 
           selectedDate.getFullYear() === year;
  };
  
  const isToday = (day) => {
    if (!day) return false;
    const today = new Date();
    return today.getDate() === day && 
           today.getMonth() === month && 
           today.getFullYear() === year;
  };

  return (
    <div className="relative" ref={calendarRef}>
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          placeholder="YYYY-MM-DD"
          className="input text-sm py-1 w-28"
        />
        <button
          type="button"
          onClick={() => !disabled && setShowCalendar(!showCalendar)}
          disabled={disabled}
          className="p-1.5 bg-gray-100 hover:bg-gray-200 rounded text-gray-600 disabled:opacity-50"
        >
          <Calendar className="h-4 w-4" />
        </button>
      </div>
      
      {showCalendar && (
        <div className="absolute z-50 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-3 w-72">
          {/* Month/Year Header with Dropdowns */}
          <div className="flex items-center justify-between mb-3 gap-2">
            <button
              type="button"
              onClick={handlePrevMonth}
              className="p-1 hover:bg-gray-100 rounded text-lg"
            >
              ←
            </button>
            
            {/* Month Dropdown */}
            <FilterSelect
              className="w-[132px]"
              value={month}
              options={monthOptions}
              onChange={(nextValue) => setCurrentMonth(new Date(year, Number(nextValue)))}
              searchable={false}
              usePortal={false}
            />
            
            {/* Year Dropdown */}
            <FilterSelect
              className="w-[102px]"
              value={year}
              options={yearOptions}
              onChange={(nextValue) => setCurrentMonth(new Date(Number(nextValue), month))}
              searchable={false}
              usePortal={false}
            />
            
            <button
              type="button"
              onClick={handleNextMonth}
              className="p-1 hover:bg-gray-100 rounded text-lg"
            >
              →
            </button>
          </div>
          
          {/* Day Headers */}
          <div className="grid grid-cols-7 gap-1 mb-1">
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => (
              <div key={d} className="text-center text-xs text-gray-500 font-medium py-1">
                {d}
              </div>
            ))}
          </div>
          
          {/* Days */}
          <div className="grid grid-cols-7 gap-1">
            {days.map((day, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => day && handleDateSelect(day)}
                disabled={!day}
                className={`
                  h-8 w-8 text-sm rounded flex items-center justify-center
                  ${!day ? 'invisible' : ''}
                  ${isSelected(day) ? 'bg-blue-500 text-white' : ''}
                  ${isToday(day) && !isSelected(day) ? 'bg-blue-100 text-blue-700' : ''}
                  ${day && !isSelected(day) ? 'hover:bg-gray-100' : ''}
                `}
              >
                {day}
              </button>
            ))}
          </div>
          
          {/* Quick Select Today */}
          <div className="mt-3 pt-2 border-t border-gray-100">
            <button
              type="button"
              onClick={() => {
                const today = new Date();
                const year = today.getFullYear();
                const month = String(today.getMonth() + 1).padStart(2, '0');
                const day = String(today.getDate()).padStart(2, '0');
                onChange(`${year}-${month}-${day}`);
                setShowCalendar(false);
              }}
              className="w-full text-sm text-blue-600 hover:text-blue-700 py-1"
            >
              Today
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Separate QuantityInput component to prevent focus loss
const QuantityInput = ({ value, onChange, disabled = false }) => {
  const [localValue, setLocalValue] = useState(value?.toString() || '');
  const [isFocused, setIsFocused] = useState(false);
  
  useEffect(() => {
    if (isFocused) return;
    setLocalValue(value?.toString() || '');
  }, [isFocused, value]);
  
  const handleChange = (e) => {
    // Allow only numbers
    const val = e.target.value.replace(/[^0-9]/g, '');
    setLocalValue(val);
  };
  
  const handleBlur = () => {
    setIsFocused(false);
    onChange(localValue);
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      onChange(localValue);
      e.target.blur();
    }
  };
  
  return (
    <input
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      value={localValue}
      onChange={handleChange}
      onFocus={() => setIsFocused(true)}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      disabled={disabled}
      placeholder="0"
      className="input w-24 text-center mx-auto block"
    />
  );
};

const parseInventoryCountValue = (value) => {
  const numeric = Number.parseInt(value, 10);
  return Number.isFinite(numeric) && numeric >= 0 ? numeric : 0;
};

const hasInventoryCountValue = (data = {}) => (
  ['shopCount', 'shop_count', 'warehouseCount', 'warehouse_count', 'totalQuantity', 'total_quantity', 'quantity']
    .some((key) => Object.prototype.hasOwnProperty.call(data || {}, key))
);

/** Ignore log rows with no count payload — avoids hydrating {} into all zeros and clobbering drafts. */
const shouldApplyDailyInventoryLogData = (data = {}) => {
  if (!data || typeof data !== 'object') return false;
  if (hasInventoryCountValue(data)) return true;
  return Object.prototype.hasOwnProperty.call(data, 'quantity');
};

const normalizeInventoryCountData = (data = {}) => {
  const hasSplitCounts = ['shopCount', 'shop_count', 'warehouseCount', 'warehouse_count', 'totalQuantity', 'total_quantity']
    .some((key) => Object.prototype.hasOwnProperty.call(data || {}, key));
  const legacyQuantity = parseInventoryCountValue(data?.quantity);
  const shopCount = hasSplitCounts ? parseInventoryCountValue(data?.shopCount ?? data?.shop_count) : legacyQuantity;
  const persistedTotalQuantity = hasSplitCounts
    ? parseInventoryCountValue(data?.totalQuantity ?? data?.total_quantity)
    : legacyQuantity;
  const hasShopCount = Object.prototype.hasOwnProperty.call(data || {}, 'shopCount')
    || Object.prototype.hasOwnProperty.call(data || {}, 'shop_count');
  const hasWarehouseCount = Object.prototype.hasOwnProperty.call(data || {}, 'warehouseCount')
    || Object.prototype.hasOwnProperty.call(data || {}, 'warehouse_count');
  const hasExplicitSplitCount = hasShopCount || hasWarehouseCount;
  const normalizedShopCount = hasSplitCounts
    ? (hasShopCount ? shopCount : persistedTotalQuantity)
    : legacyQuantity;
  const warehouseCount = hasSplitCounts
    ? (hasWarehouseCount ? parseInventoryCountValue(data?.warehouseCount ?? data?.warehouse_count) : 0)
    : 0;

  return {
    shopCount: normalizedShopCount,
    warehouseCount,
    totalQuantity: hasSplitCounts
      ? (hasExplicitSplitCount ? (normalizedShopCount + warehouseCount) : persistedTotalQuantity)
      : legacyQuantity,
  };
};

// Separate TemperatureInput component to prevent focus loss (allows negative and decimal)
const TemperatureInput = ({ value, onChange, disabled = false }) => {
  const [localValue, setLocalValue] = useState(value?.toString() || '');
  const [isFocused, setIsFocused] = useState(false);
  
  useEffect(() => {
    if (isFocused) return;
    setLocalValue(value?.toString() || '');
  }, [isFocused, value]);
  
  const handleChange = (e) => {
    let val = e.target.value;
    
    if (val === '' || val === '-') {
      setLocalValue(val);
      return;
    }
    
    const isValid = /^-?\d*\.?\d*$/.test(val);
    
    if (isValid) {
      setLocalValue(val);
    }
  };
  
  const handleBlur = () => {
    setIsFocused(false);
    const numVal = parseFloat(localValue);
    onChange(isNaN(numVal) ? '' : numVal);
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      const numVal = parseFloat(localValue);
      onChange(isNaN(numVal) ? '' : numVal);
      e.target.blur();
    }
  };
  
  return (
    <input
      type="text"
      inputMode="decimal"
      value={localValue}
      onChange={handleChange}
      onFocus={() => setIsFocused(true)}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      placeholder="°C"
      className="input w-24 text-center mx-auto block"
    />
  );
};

// Daily task definitions
const DAILY_TASKS = [
  { key: 'dateCheck', label: 'Date Check', icon: Calendar, color: 'bg-blue-500', textColor: 'text-blue-600' },
  { key: 'shortlifeDateCheck', label: 'Short-life Check', icon: AlertCircle, color: 'bg-orange-500', textColor: 'text-orange-600' },
  { key: 'produceDateCheck', label: 'Produce Check', icon: Package, color: 'bg-green-500', textColor: 'text-green-600' },
  { key: 'dailyInventory', label: 'Inventory Count', icon: ClipboardCheck, color: 'bg-purple-500', textColor: 'text-purple-600' },
  { key: 'temperatureCheck', label: 'Temperature', icon: Thermometer, color: 'bg-red-500', textColor: 'text-red-600' },
];

/** Overall % from all subtasks (smooth); not the same as “full tasks done” (steps of 20%). */
const formatLocalDateKey = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Utility: Get today's date key
const getTodayKey = () => formatLocalDateKey();

// Utility: Check if current time is within task window (6:30 AM - 11:30 PM)
const isWithinTaskWindow = () => {
  const now = new Date();
  const hours = now.getHours();
  const minutes = now.getMinutes();
  const currentTime = hours * 60 + minutes; // Convert to minutes since midnight
  
  const startTime = 6 * 60 + 30;  // 6:30 AM = 390 minutes
  const endTime = 23 * 60 + 30;   // 11:30 PM = 1410 minutes
  
  return currentTime >= startTime && currentTime <= endTime;
};

// Utility: Format date for display
const formatDate = (dateKey) => {
  const date = new Date(dateKey);
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

const getDateRangeValues = (start, end) => {
  const values = [];
  const current = new Date(start);
  const endDate = new Date(end);

  while (current <= endDate) {
    values.push(formatLocalDateKey(current));
    current.setDate(current.getDate() + 1);
  }

  return values;
};

const shiftDate = (dateKey, days) => {
  const [year, month, day] = String(dateKey).split('-').map(Number);
  const date = new Date(year, (month || 1) - 1, day || 1);
  date.setDate(date.getDate() + days);
  return formatLocalDateKey(date);
};

const normalizeDateKeyValue = (value) => {
  const normalized = String(value || '').trim();
  if (!normalized) return null;

  const directMatch = normalized.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (directMatch) {
    return `${directMatch[1]}-${directMatch[2]}-${directMatch[3]}`;
  }

  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) return null;
  return formatLocalDateKey(parsed);
};

/** Map log subtask_id (may be legacy SKU) to current assignment product id. */
const buildSubtaskKeyResolver = (assignments = {}) => {
  const idSet = new Set();
  const skuToId = new Map();
  Object.values(assignments).forEach((products) => {
    (products || []).forEach((product) => {
      const id = String(product?.id || '').trim();
      const sku = String(product?.sku || '').trim();
      if (id) idSet.add(id);
      if (sku && id) skuToId.set(sku, id);
    });
  });
  return (rawSubtaskId, taskType) => {
    const key = String(rawSubtaskId || '').trim();
    if (!key) return key;
    if (taskType === 'temperatureCheck') {
      return normalizeTemperatureComplianceSubtaskId(key);
    }
    if (idSet.has(key)) return key;
    if (skuToId.has(key)) return skuToId.get(key);
    return key;
  };
};

const getTaskProductDedupeKey = (product = {}) => (
  String(
    product?.sku
    || product?.product_id
    || product?.ProductId
    || product?.id
    || ''
  ).trim().toLowerCase()
);

const dedupeTaskProducts = (items = []) => {
  const map = new Map();
  (items || []).forEach((product) => {
    const key = getTaskProductDedupeKey(product);
    if (!key) return;
    if (!map.has(key)) {
      map.set(key, product);
    }
  });
  return Array.from(map.values());
};

// Keep daily task windows stable: dateCheck/inventory slices depend on product order.
const sortTaskProductsDeterministically = (items = []) => (
  [...(items || [])].sort((left, right) => {
    const lSku = String(left?.sku || '').trim().toLowerCase();
    const rSku = String(right?.sku || '').trim().toLowerCase();
    if (lSku && rSku && lSku !== rSku) return lSku.localeCompare(rSku);
    const lId = String(left?.id || '').trim().toLowerCase();
    const rId = String(right?.id || '').trim().toLowerCase();
    if (lId !== rId) return lId.localeCompare(rId);
    return String(left?.name || '').localeCompare(String(right?.name || ''));
  })
);

const uniqSubtaskIdsForSummary = (ids = []) => [...new Set((ids || []).map(String).filter(Boolean))];

const buildComplianceLogsFingerprint = (logs = []) =>
  (Array.isArray(logs) ? logs : [])
    .map((log) => `${log.task_type || ''}:${log.subtask_id || ''}:${log.updated_at || log.created_at || ''}`)
    .sort()
    .join('|');

const readSyncSourceField = (product, fieldNames = []) => {
  const src = product?.sync_source && typeof product.sync_source === 'object' ? product.sync_source : {};
  const lowered = new Map(
    Object.entries(src).map(([key, value]) => [String(key).toLowerCase(), value])
  );
  for (const name of fieldNames) {
    const v = lowered.get(String(name).toLowerCase());
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return undefined;
};

/** Match backend: prefer SQL snapshot in sync_source; only PUBLISHED / true / 1 count as published. */
const isProduceCatalogPublished = (product) => {
  const raw = readSyncSourceField(product, ['Published', 'published'])
    ?? product.published
    ?? product.Published
    ?? product.is_published
    ?? product.isPublished;
  if (raw === null || raw === undefined || raw === '') return false;
  if (typeof raw === 'boolean') return raw === true;
  if (typeof raw === 'number') return raw === 1;
  const upper = String(raw).trim().toUpperCase();
  if (upper === 'PUBLISHED') return true;
  if (upper === 'UNPUBLISHED') return false;
  return false;
};

/** Returns true only if the product is explicitly marked as unpublished (UNPUBLISHED / false / 0). */
const isExplicitlyUnpublished = (product) => {
  const raw = readSyncSourceField(product, ['Published', 'published'])
    ?? product.published
    ?? product.Published
    ?? product.is_published
    ?? product.isPublished;
  if (raw === null || raw === undefined || raw === '') return false;
  if (typeof raw === 'boolean') return raw === false;
  if (typeof raw === 'number') return raw === 0;
  const upper = String(raw).trim().toUpperCase();
  return upper === 'UNPUBLISHED' || upper === 'FALSE' || upper === '0';
};

const getProduceParentCategoryLabel = (product) => {
  const fromSync = String(
    readSyncSourceField(product, [
      'ParentCategory',
      'parentCategory',
      'parent_category',
      'ParentCategoryName',
    ]) ?? ''
  ).trim();
  if (fromSync) return fromSync;
  return String(
    product.parentCategory
    ?? product.parentCategoryName
    ?? product.parent_category
    ?? product.parent_category_id
    ?? product.ParentCategory
    ?? ''
  ).trim();
};

const isFruitsAndVegetablesParentCategory = (product) => {
  const label = getProduceParentCategoryLabel(product);
  if (!label) return false;
  return label.toLowerCase() === 'fruits and vegetables';
};

/** Match backend listProducts(produce): only rows tied to this store (exclude vendor-wide null store_id). */
const isProduceCatalogStoreScoped = (product, storeId) => (
  !storeId || String(product.store_id ?? product.storeId ?? '').trim() === String(storeId).trim()
);

const buildTaskWindow = (items = [], offset = 0, size = 0) => {
  const list = Array.isArray(items) ? items : [];
  const limit = Math.max(0, Number(size) || 0);
  if (!list.length || limit <= 0) return [];
  const start = Math.max(0, Number(offset) || 0);
  return list.slice(start, start + limit);
};

const getFallbackDateCheckProducts = (sortedProducts = []) => buildTaskWindow(sortedProducts, 0, 30);

const buildDailyInventoryDraftKey = (storeId, dateKey) => (
  `dailyInventoryDraft::${String(storeId || 'unknown')}::${String(dateKey || '')}`
);

const readDailyInventoryDraft = (storageKey) => {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch (_) {
    return {};
  }
};

const writeDailyInventoryDraft = (storageKey, inventoryData = {}) => {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(inventoryData || {}));
  } catch (_) {
    // Ignore storage failures to avoid blocking task entry.
  }
};

const DailyComplianceTasks = ({ mode = 'auto' }) => {
  const { isAdmin, isManager, isSupervisor } = useAuthStore();
  const { selectedStore } = useStoreContextStore();
  const isSupervisorUser = isSupervisor();
  const roleIsStaff = !isAdmin() && !isManager() && !isSupervisorUser;
  const showReportView = mode === 'report' || (mode === 'auto' && !roleIsStaff);
  const isStaff = !showReportView;
  const canFilterAllStores = false;
  const selectedLegacyStoreId = selectedStore?.legacyStoreId || selectedStore?.legacy_store_id || '';
  const selectedStoreInfoId = selectedStore?.storeInfoId || selectedStore?.store_info_id || '';
  const selectedVendorId = selectedStore?.vendorId || selectedStore?.vendor_id || '';
  const currentUserStoreId = selectedStoreInfoId || selectedLegacyStoreId || '';
  const currentStoreName = selectedStore?.name || selectedStore?.storeName || selectedStore?.store_code || selectedStore?.storeCode || (currentUserStoreId ? `Store ${currentUserStoreId}` : 'Select store from header');
  const selectedStoreContextKey = [
    currentUserStoreId || '',
    selectedStoreInfoId || '',
    selectedVendorId || '',
  ].join('::');
  const todayKey = getTodayKey();
  const supervisorReportMinDate = shiftDate(todayKey, -7);
  const stores = useMemo(() => (
    currentUserStoreId
      ? [{
          id: currentUserStoreId,
          store_info_id: selectedStoreInfoId || selectedStore?.store_info_id || null,
          storeInfoId: selectedStoreInfoId || selectedStore?.storeInfoId || null,
          vendor_id: selectedVendorId || null,
          vendorId: selectedVendorId || null,
          name: currentStoreName,
          code: selectedStore?.storeCode || selectedStore?.store_code || '',
        }]
      : []
  ), [currentStoreName, currentUserStoreId, selectedStore, selectedStoreInfoId, selectedVendorId]);
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [produceProducts, setProduceProducts] = useState([]);
  const [reportProducts, setReportProducts] = useState([]);
  const [reportProductsReady, setReportProductsReady] = useState(false);
  const [assignedTaskProducts, setAssignedTaskProducts] = useState({
    dateCheck: [],
    dailyInventory: [],
    shortlifeDateCheck: [],
    produceDateCheck: [],
    temperatureCheck: [],
  });
  const [reportRows, setReportRows] = useState([]);
  const [reportLogs, setReportLogs] = useState([]);
  const dailyInventoryDraftKey = useMemo(
    () => buildDailyInventoryDraftKey(currentUserStoreId, getTodayKey()),
    [currentUserStoreId]
  );

  const userStore = stores.find((store) => String(store.id) === String(currentUserStoreId)) || {
    id: currentUserStoreId,
    store_info_id: selectedStoreInfoId || selectedStore?.store_info_id || null,
    storeInfoId: selectedStoreInfoId || selectedStore?.storeInfoId || null,
    vendor_id: selectedVendorId || null,
    vendorId: selectedVendorId || null,
    name: currentStoreName,
    code: selectedStore?.storeCode || selectedStore?.store_code || ''
  };
  
  const [activeTab, setActiveTab] = useState('dateCheck');
  const [completedTasks, setCompletedTasks] = useState({});
  const [taskStats, setTaskStats] = useState({});
  const [summaryReady, setSummaryReady] = useState(false);
  
  // Data states
  const [dateCheckData, setDateCheckData] = useState({});
  const [inventoryData, setInventoryData] = useState({});
  const [shortlifeData, setShortlifeData] = useState({});
  const [temperatureData, setTemperatureData] = useState({});
  const [produceData, setProduceData] = useState({});
  const [assignmentsReady, setAssignmentsReady] = useState(false);
  const temperatureZones = useMemo(() => {
    const source = assignedTaskProducts.temperatureCheck || [];

    return (source || []).map((zone) => {
      const id = String(zone.id || zone.assetId || zone.asset_id || '').trim();
      const min = zone.min ?? zone.temperatureMin ?? zone.temperature_min;
      const max = zone.max ?? zone.temperatureMax ?? zone.temperature_max;
      return {
        ...zone,
        id,
        name: zone.name || zone.assetName || zone.asset_name || id,
        location: zone.location || '-',
        min: min === null || min === undefined || min === '' ? null : Number(min),
        max: max === null || max === undefined || max === '' ? null : Number(max),
      };
    }).filter((zone) => zone.id);
  }, [assignedTaskProducts.temperatureCheck]);
  const sortedTaskProducts = useMemo(
    () => {
      const filtered = products.filter((p) => {
        if (isExplicitlyUnpublished(p)) return false;
        const parentCat = String(
          p.ParentCategory || p.parent_category || p.parent_category_id || ''
        ).toLowerCase();
        const cat = String(
          p.Category || p.category || p.category_id || ''
        ).toLowerCase();
        if (parentCat.includes('egg and dairy') || cat.includes('bread')) return false;
        if (isFruitsAndVegetablesParentCategory(p)) return false;
        return true;
      });
      return sortTaskProductsDeterministically(filtered);
    },
    [products]
  );
  const complianceSummaryOptions = useMemo(() => {
    if (isStaff) {
      if (!assignmentsReady) return null;
      return buildExpectedSubtaskMap(assignedTaskProducts);
    }

    if (!Array.isArray(products) || products.length === 0) return null;
    const shortlife = assignedTaskProducts.shortlifeDateCheck?.length
      ? assignedTaskProducts.shortlifeDateCheck
      : products.filter((product) => {
        if (isExplicitlyUnpublished(product)) return false;
        const parentCategory = String(
          product.ParentCategory || product.parent_category || product.parent_category_id || ''
        ).toLowerCase();
        const category = String(
          product.Category || product.category || product.category_id || ''
        ).toLowerCase();
        return parentCategory.includes('egg and dairy') || category.includes('bread');
      }).slice(0, 20);
    const produce = assignedTaskProducts.produceDateCheck?.length
      ? assignedTaskProducts.produceDateCheck
      : (produceProducts.length
        ? produceProducts
        : sortedTaskProducts.filter((product) => (
          isProduceCatalogPublished(product)
          && isFruitsAndVegetablesParentCategory(product)
          && isProduceCatalogStoreScoped(product, userStore.id || currentUserStoreId)
        )));
    const dateCheckIds = assignedTaskProducts.dateCheck?.length
      ? assignedTaskProducts.dateCheck.map((p) => String(p.id))
      : getFallbackDateCheckProducts(sortedTaskProducts).map((p) => String(p.id));
    const inventoryIds = assignedTaskProducts.dailyInventory?.length
      ? assignedTaskProducts.dailyInventory.map((p) => String(p.id))
      : [];
    return {
      dateCheck: uniqSubtaskIdsForSummary(dateCheckIds),
      dailyInventory: uniqSubtaskIdsForSummary(inventoryIds),
      shortlifeDateCheck: uniqSubtaskIdsForSummary(shortlife.map((p) => String(p.id))),
      produceDateCheck: uniqSubtaskIdsForSummary(produce.map((p) => String(p.id))),
      temperatureCheck: uniqSubtaskIdsForSummary(temperatureZones.map((z) => z.id)),
    };
  }, [
    products,
    produceProducts,
    sortedTaskProducts,
    assignedTaskProducts,
    userStore.id,
    isStaff,
    assignmentsReady,
    currentUserStoreId,
    temperatureZones,
  ]);
  const loadAllDataRef = useRef(null);
  const refreshSummaryRef = useRef(null);
  const latestFetchedLogsRef = useRef(null);
  const assignedTaskProductsRef = useRef(assignedTaskProducts);
  const queryClient = useQueryClient();
  const appliedDayBoardKeyRef = useRef('');
  const emptyTemperatureRefetchKeyRef = useRef('');
  const yesterdayAppliedRef = useRef(false);
  const yesterdayLogsRef = useRef([]);
  const lastRemoteLogsFingerprintRef = useRef('');
  const prevStoreContextKeyRef = useRef(selectedStoreContextKey);

  const staffDayBoardQueryKey = useMemo(
    () => [
      'complianceDayBoard',
      String(userStore?.id || ''),
      String(selectedStoreInfoId || ''),
      String(selectedVendorId || ''),
      todayKey,
      activeTab,
    ],
    [activeTab, selectedStoreInfoId, selectedVendorId, todayKey, userStore?.id]
  );

  const fetchStaffDayBoard = useCallback(async () => {
    const response = await complianceTasksAPI.getDayBoard({
      date: todayKey,
      storeId: userStore.id,
      storeInfoId: selectedStoreInfoId || undefined,
      vendorId: selectedVendorId || undefined,
      taskType: activeTab,
    });
    return response.data?.data || null;
  }, [activeTab, selectedStoreInfoId, selectedVendorId, todayKey, userStore.id]);

  const {
    data: dayBoardPayload,
    isLoading: isDayBoardLoading,
    isFetching: isDayBoardFetching,
    refetch: refetchDayBoard,
  } = useQuery(staffDayBoardQueryKey, fetchStaffDayBoard, {
    enabled: isStaff && Boolean(userStore?.id),
    staleTime: 15 * 60 * 1000,
    cacheTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 1,
  });

  useEffect(() => {
    if (prevStoreContextKeyRef.current === selectedStoreContextKey) return;
    prevStoreContextKeyRef.current = selectedStoreContextKey;
    appliedDayBoardKeyRef.current = '';
    yesterdayAppliedRef.current = false;
    yesterdayLogsRef.current = [];
    lastRemoteLogsFingerprintRef.current = '';
    setSummaryReady(false);
    setTaskStats({});
    setCompletedTasks({});
    setAssignmentsReady(false);
    setAssignedTaskProducts({
      dateCheck: [],
      dailyInventory: [],
      shortlifeDateCheck: [],
      produceDateCheck: [],
      temperatureCheck: [],
    });
    if (currentUserStoreId) {
      queryClient.removeQueries(['complianceDayBoard'], { exact: false });
      queryClient.removeQueries(['complianceTaskLogs'], { exact: false });
    }
  }, [currentUserStoreId, queryClient, selectedStoreContextKey]);

  const inventorySyncTimersRef = useRef({});
  const pendingSyncsRef = useRef(new Set());

  useEffect(() => {
    assignedTaskProductsRef.current = assignedTaskProducts;
  }, [assignedTaskProducts]);
  const reportExportHandlerRef = useRef(() => {});
  
  // Admin/Manager report view
  const [reportStartDate, setReportStartDate] = useState(todayKey);
  const [reportEndDate, setReportEndDate] = useState(todayKey);
  const [reportStore, setReportStore] = useState(currentUserStoreId || '');
  const [reportUser, setReportUser] = useState('all');
  const [reportTaskType, setReportTaskType] = useState('all');
  const [activityStatusFilter, setActivityStatusFilter] = useState('all');

  // Load data on mount only (not on every store change)
  useEffect(() => {
    const loadReferenceData = async () => {
      try {
        if (roleIsStaff) {
          setUsers([]);
          return;
        }

        try {
          const usersRes = await usersAPI.getAll({
            limit: 500,
            ...(currentUserStoreId ? { storeId: currentUserStoreId } : {})
          });
          setUsers(usersRes.data?.data || []);
        } catch (userError) {
          console.error('Failed to load report users:', userError);
          setUsers([]);
        }
      } catch (error) {
        console.error('Failed to load task reference data:', error);
      }
    };

    loadReferenceData();
  }, [currentUserStoreId, roleIsStaff]);

  useEffect(() => {
    const loadProducts = async () => {
      // Report view loads its own catalog. Staff `products` are owned by loadStoreProducts only —
      // never clear them here or totals flip (e.g. 92 quota vs ~168 real rows) within seconds.
      if (!showReportView) {
        setReportProductsReady(false);
        return;
      }

      if (!currentUserStoreId && !selectedVendorId) {
        setReportProductsReady(true);
        setReportProducts([]);
        return;
      }

      setReportProductsReady(false);
      setReportProducts([]);

      try {
        const fallbackRes = await productsAPI.getAllCached({
          limit: 2000,
          isActive: 'all',
          ...(selectedStoreInfoId ? { storeInfoId: selectedStoreInfoId } : {}),
          ...(!selectedStoreInfoId && selectedLegacyStoreId ? { storeId: selectedLegacyStoreId } : {}),
          ...(selectedVendorId ? { vendorId: selectedVendorId } : {}),
        });
        const allFetchedProducts = fallbackRes.data?.data || [];
        const normalizedAllProducts = allFetchedProducts.map((product) => {
          const resolvedName =
            product.name
            || product.product_name
            || product.product
            || product.Name
            || product.Product
            || product.sku;
          const resolvedCategory = product.category || product.category_id || product.Category || null;
          const resolvedParentCategory =
            product.parent_category || product.parent_category_id || product.ParentCategory || null;
          const resolvedStockQuantity = product.stock_quantity ?? product.stock_qty ?? 0;
          const resolvedSellerName =
            product.seller_name || product.seller || product.Seller || product.vendor_id || product.VendorId || null;

          return {
            ...product,
            id: product.id || product.product_id || product.ProductId || product.sku,
            name: resolvedName,
            category: resolvedCategory,
            parentCategory: resolvedParentCategory,
            stockQuantity: resolvedStockQuantity,
            triggerQuantity: product.trigger_qty ?? product.TriggerQty ?? 0,
            sellerId: product.seller_id || product.vendor_id || product.VendorId || null,
            sellerName: resolvedSellerName,
          };
        });
        const dedupedProducts = dedupeTaskProducts(normalizedAllProducts);
        setReportProducts(dedupedProducts);
      } catch (error) {
        console.error('Failed to load products for report view:', error);
        setReportProducts([]);
      } finally {
        setReportProductsReady(true);
      }
    };

    loadProducts();
  }, [currentUserStoreId, selectedLegacyStoreId, selectedStoreInfoId, selectedVendorId, showReportView]);

  // Report / manager views still use full catalog; staff tasks use workspace assignments.
  useEffect(() => {
    if (!currentUserStoreId || isStaff) return;

    const loadStoreProducts = async () => {
      try {
        const requestParams = {
          isActive: 'true',
          // Keep this high enough to include all active store products.
          limit: 5000,
        };
        const response = await productsAPI.getAll({
          ...requestParams,
          storeId: currentUserStoreId,
        });
        let allProducts = response.data?.data || [];

        // Some deployments return 0 rows for store-scoped product queries while
        // global list still contains store-mapped records. Fall back and filter
        // locally so daily tasks (including produce) still load correctly.
        if (!allProducts.length) {
          const fallbackResponse = await productsAPI.getAll(requestParams);
          const fallbackProducts = fallbackResponse.data?.data || [];
          allProducts = fallbackProducts.filter((product) => (
            String(product.store_id || product.storeId || '').trim() === String(currentUserStoreId)
          ));
        }
        const normalizedProducts = allProducts
          .map((product) => {
            const resolvedId = (
              product.id
              || product.product_id
              || product.ProductId
              || product.sku
            );
            const resolvedSku = product.sku || product.SKU || product.product_id || product.ProductId || '';
            const resolvedName = product.name || product.product_name || product.product || product.Name || resolvedSku;

            return {
              ...product,
              id: String(resolvedId || '').trim(),
              sku: String(resolvedSku || '').trim(),
              name: String(resolvedName || '').trim(),
            };
          })
          .filter((product) => product.id);

        setProducts(sortTaskProductsDeterministically(dedupeTaskProducts(normalizedProducts)));

        // Source of truth for produce list/count: backend taskType=produce
        // (already applies store/category/published rules server-side).
        const produceResponse = await productsAPI.getAll({
          isActive: 'true',
          taskType: 'produce',
          limit: 5000,
          storeId: currentUserStoreId,
        });
        const produceRows = produceResponse.data?.data || [];
        const normalizedProduceRows = produceRows
          .map((product) => {
            const resolvedId = (
              product.id
              || product.product_id
              || product.ProductId
              || product.sku
            );
            const resolvedSku = product.sku || product.SKU || product.product_id || product.ProductId || '';
            const resolvedName = product.name || product.product_name || product.product || product.Name || resolvedSku;
            return {
              ...product,
              id: String(resolvedId || '').trim(),
              sku: String(resolvedSku || '').trim(),
              name: String(resolvedName || '').trim(),
            };
          })
          .filter((product) => product.id);
        setProduceProducts(sortTaskProductsDeterministically(dedupeTaskProducts(normalizedProduceRows)));
      } catch (error) {
        console.error('Failed to load store products:', error);
        setProducts([]);
        setProduceProducts([]);
      }
    };

    loadStoreProducts();
  }, [currentUserStoreId, isStaff]);

  useEffect(() => {
    setReportStore(currentUserStoreId || '');
  }, [currentUserStoreId]);

  useEffect(() => {
    if (!isSupervisorUser) return;

    if (reportStartDate < supervisorReportMinDate) {
      setReportStartDate(supervisorReportMinDate);
    }
    if (reportEndDate < supervisorReportMinDate) {
      setReportEndDate(supervisorReportMinDate);
    }
    if (reportStartDate > todayKey) {
      setReportStartDate(todayKey);
    }
    if (reportEndDate > todayKey) {
      setReportEndDate(todayKey);
    }
  }, [isSupervisorUser, reportEndDate, reportStartDate, supervisorReportMinDate, todayKey]);

  // Day board loads via react-query cache (no polling). Progress updates locally after each save.

  useEffect(() => () => {
    Object.values(inventorySyncTimersRef.current || {}).forEach((timerId) => {
      clearTimeout(timerId);
    });
    inventorySyncTimersRef.current = {};
  }, []);
  
  const applySummaryFromServer = useCallback((summaryPayload) => {
    const { taskStats: nextStats, completedTasks: nextCompleted } = applyComplianceSummaryToTasks(
      summaryPayload,
      DAILY_TASKS
    );
    setTaskStats(nextStats);
    setCompletedTasks(nextCompleted);
    setSummaryReady(true);
  }, []);

  const hydrateTaskStateFromLogs = useCallback((
    mergedLogs,
    yesterdayLogs,
    { preservePending = true, resolveSubtaskKey = null } = {}
  ) => {
    const mapKey = (log) => {
      const taskType = log.task_type;
      const raw = log.subtask_id;
      if (typeof resolveSubtaskKey === 'function') {
        return resolveSubtaskKey(raw, taskType);
      }
      if (taskType === 'temperatureCheck') {
        return normalizeTemperatureComplianceSubtaskId(String(raw || ''));
      }
      return String(raw || '');
    };

    const yMerged = mergeComplianceLogsByLatestSubtask(yesterdayLogs || []);
    const yInventory = {};

    yMerged.forEach((log) => {
      const key = mapKey(log);
      const data = log.data || {};
      if (log.task_type === 'dailyInventory' && shouldApplyDailyInventoryLogData(data)) {
        yInventory[key] = normalizeInventoryCountData(data);
      }
    });

    const nextDateCheck = {};
    const nextInventory = {};
    const nextShortlife = {};
    const nextTemperature = {};
    const nextProduce = {};

    (mergedLogs || []).forEach((log) => {
      const key = mapKey(log);
      const data = log.data || {};

      if (log.task_type === 'dateCheck') {
        nextDateCheck[key] = {
          date: data.date || '',
          notAvailable: !!data.notAvailable,
        };
      } else if (log.task_type === 'dailyInventory') {
        if (shouldApplyDailyInventoryLogData(data)) {
          nextInventory[key] = normalizeInventoryCountData(data);
        }
      } else if (log.task_type === 'shortlifeDateCheck') {
        nextShortlife[key] = { checked: !!data.checked, time: log.completed_at };
      } else if (log.task_type === 'produceDateCheck') {
        nextProduce[key] = {
          status: data.notAvailable ? '' : (data.status || ''),
          notAvailable: !!data.notAvailable,
          time: log.completed_at,
        };
      } else if (log.task_type === 'temperatureCheck') {
        const reading = parseTemperatureComplianceReading(data);
        if (reading !== null && key) {
          nextTemperature[key] = { temp: reading, time: log.completed_at };
        }
      }
    });

    const pending = preservePending ? pendingSyncsRef.current : new Set();
    setDateCheckData((prev) => {
      const merged = { ...nextDateCheck };
      if (preservePending) {
        for (const id of Object.keys(prev)) {
          if (pending.has(`dateCheck::${id}`)) merged[id] = prev[id];
        }
      }
      return merged;
    });
    const draftInventory = readDailyInventoryDraft(dailyInventoryDraftKey);
    const mergedInventory = {
      ...yInventory,
      ...nextInventory,
      ...draftInventory,
    };
    setInventoryData(mergedInventory);
    setShortlifeData((prev) => {
      const merged = { ...nextShortlife };
      if (preservePending) {
        for (const id of Object.keys(prev)) {
          if (pending.has(`shortlifeDateCheck::${id}`)) merged[id] = prev[id];
        }
      }
      return merged;
    });
    setTemperatureData((prev) => {
      const merged = { ...nextTemperature };
      if (preservePending) {
        for (const id of Object.keys(prev)) {
          if (pending.has(`temperatureCheck::${id}`)) merged[id] = prev[id];
        }
      }
      return merged;
    });
    setProduceData((prev) => {
      const merged = { ...nextProduce };
      if (preservePending) {
        for (const id of Object.keys(prev)) {
          if (pending.has(`produceDateCheck::${id}`)) merged[id] = prev[id];
        }
      }
      return merged;
    });
    writeDailyInventoryDraft(dailyInventoryDraftKey, mergedInventory);
  }, [dailyInventoryDraftKey]);

  const recomputeLocalSummary = useCallback((logs, assignments) => {
    const assignmentBundle = assignments || assignedTaskProductsRef.current || {};
    const summaryOptions = buildExpectedSubtaskMap(assignmentBundle);
    const hasAssignments = Object.values(summaryOptions).some(
      (ids) => Array.isArray(ids) && ids.length > 0
    );
    if (!hasAssignments) return;

    const mergedLogs = mergeComplianceLogsByLatestSubtask(logs || []);
    const summary = summarizeDailyCompliance(mergedLogs, summaryOptions);
    applySummaryFromServer(summary);
  }, [applySummaryFromServer]);

  const applyAssignmentsAndLogs = useCallback(({
    assignments,
    logs,
    yesterdayLogs = [],
    summary = null,
    preservePending = true,
  }) => {
    const normalized = orderAssignmentBundleForDisplay(
      normalizeAssignmentBundle({
        dateCheck: assignments?.dateCheck || [],
        shortlifeDateCheck: assignments?.shortlifeDateCheck || [],
        dailyInventory: assignments?.dailyInventory || [],
        produceDateCheck: assignments?.produceDateCheck || [],
        temperatureCheck: assignments?.temperatureCheck || [],
      }),
      userStore?.id,
      todayKey
    );
    setAssignedTaskProducts(normalized);
    setAssignmentsReady(true);
    assignedTaskProductsRef.current = normalized;

    latestFetchedLogsRef.current = logs || [];
    lastRemoteLogsFingerprintRef.current = buildComplianceLogsFingerprint(logs || []);
    const mergedLogs = mergeComplianceLogsByLatestSubtask(logs || []);
    const resolveSubtaskKey = buildSubtaskKeyResolver(normalized);
    hydrateTaskStateFromLogs(mergedLogs, yesterdayLogs, { preservePending, resolveSubtaskKey });
    if (summary?.tasks) {
      applySummaryFromServer(summary);
    } else {
      recomputeLocalSummary(logs, normalized);
    }
  }, [applySummaryFromServer, hydrateTaskStateFromLogs, recomputeLocalSummary, todayKey, userStore?.id]);

  useLayoutEffect(() => {
    if (!isStaff || !dayBoardPayload || !userStore?.id) return;
    const applyKey = `${userStore.id}::${todayKey}::${activeTab}`;
    if (appliedDayBoardKeyRef.current === applyKey) return;
    appliedDayBoardKeyRef.current = applyKey;
    applyAssignmentsAndLogs({
      assignments: dayBoardPayload.assignments || {},
      logs: dayBoardPayload.logs || [],
      yesterdayLogs: [],
      summary: dayBoardPayload.summary || null,
    });
  }, [activeTab, applyAssignmentsAndLogs, dayBoardPayload, isStaff, userStore?.id, todayKey]);

  useEffect(() => {
    if (activeTab !== 'temperatureCheck') return;
    if (!dayBoardPayload || isDayBoardLoading || isDayBoardFetching) return;

    const temperatureAssignments = dayBoardPayload.assignments?.temperatureCheck;
    const refetchKey = `${selectedStoreContextKey}::${todayKey}`;

    if (
      Array.isArray(temperatureAssignments)
      && temperatureAssignments.length === 0
      && emptyTemperatureRefetchKeyRef.current !== refetchKey
    ) {
      emptyTemperatureRefetchKeyRef.current = refetchKey;
      refetchDayBoard({ throwOnError: false });
    }
  }, [
    activeTab,
    dayBoardPayload,
    isDayBoardFetching,
    isDayBoardLoading,
    refetchDayBoard,
    selectedStoreContextKey,
    todayKey,
  ]);

  const mergeYesterdayCarryover = useCallback((yesterdayLogs = []) => {
    yesterdayLogsRef.current = yesterdayLogs || [];
    const yMerged = mergeComplianceLogsByLatestSubtask(yesterdayLogs);
    const resolveSubtaskKey = buildSubtaskKeyResolver(assignedTaskProductsRef.current);
    const yInventory = {};

    yMerged.forEach((log) => {
      const key = resolveSubtaskKey(log.subtask_id, log.task_type);
      const data = log.data || {};
      if (log.task_type === 'dailyInventory' && shouldApplyDailyInventoryLogData(data)) {
        yInventory[key] = normalizeInventoryCountData(data);
      }
    });

    if (Object.keys(yInventory).length) {
      setInventoryData((prev) => {
        const draftInventory = readDailyInventoryDraft(dailyInventoryDraftKey);
        const merged = { ...yInventory, ...prev, ...draftInventory };
        writeDailyInventoryDraft(dailyInventoryDraftKey, merged);
        return merged;
      });
    }
  }, [dailyInventoryDraftKey]);

  useEffect(() => {
    if (!isStaff || !assignmentsReady || !userStore?.id) return;

    const yesterdayQueryKey = ['complianceYesterdayLogs', String(userStore.id), todayKey, activeTab];
    const cachedYesterday = queryClient.getQueryData(yesterdayQueryKey);
    if (cachedYesterday) {
      mergeYesterdayCarryover(cachedYesterday);
      return;
    }
    if (yesterdayAppliedRef.current) return;

    let cancelled = false;
    yesterdayAppliedRef.current = true;
    (async () => {
      try {
        const response = await complianceTasksAPI.getLogs({
          date: shiftDate(todayKey, -1),
          storeId: userStore.id,
          taskType: activeTab,
        });
        if (cancelled) return;
        const yesterdayLogs = response.data?.data || [];
        yesterdayLogsRef.current = yesterdayLogs;
        queryClient.setQueryData(yesterdayQueryKey, yesterdayLogs);
        mergeYesterdayCarryover(yesterdayLogs);
      } catch (error) {
        console.error('Failed to load yesterday carryover logs:', error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activeTab, isStaff, assignmentsReady, userStore?.id, todayKey, queryClient, mergeYesterdayCarryover]);

  const refreshSummary = async () => {
    recomputeLocalSummary(latestFetchedLogsRef.current, assignedTaskProductsRef.current);
  };
  refreshSummaryRef.current = refreshSummary;

  const staffLogsQueryKey = useMemo(
    () => ['complianceTaskLogs', String(userStore?.id || ''), todayKey, activeTab],
    [activeTab, userStore?.id, todayKey]
  );

  const mergeRemoteComplianceLogs = useCallback((rawLogs = []) => {
    const logs = Array.isArray(rawLogs) ? rawLogs : [];
    const fingerprint = buildComplianceLogsFingerprint(logs);
    if (fingerprint === lastRemoteLogsFingerprintRef.current) return;
    lastRemoteLogsFingerprintRef.current = fingerprint;

    latestFetchedLogsRef.current = logs;
    const mergedLogs = mergeComplianceLogsByLatestSubtask(logs);
    const resolveSubtaskKey = buildSubtaskKeyResolver(assignedTaskProductsRef.current);
    hydrateTaskStateFromLogs(mergedLogs, yesterdayLogsRef.current, {
      preservePending: true,
      resolveSubtaskKey,
    });
    recomputeLocalSummary(logs, assignedTaskProductsRef.current);

    queryClient.setQueryData(staffDayBoardQueryKey, (prev) => {
      if (!prev || typeof prev !== 'object') return prev;
      return { ...prev, logs: mergedLogs };
    });
  }, [hydrateTaskStateFromLogs, queryClient, recomputeLocalSummary, staffDayBoardQueryKey]);

  const fetchStaffTaskLogs = useCallback(async () => {
    const response = await complianceTasksAPI.getLogs({
      date: todayKey,
      storeId: userStore.id,
      taskType: activeTab,
    });
    return response.data?.data || [];
  }, [activeTab, todayKey, userStore.id]);

  useQuery(staffLogsQueryKey, fetchStaffTaskLogs, {
    enabled: isStaff && Boolean(userStore?.id) && assignmentsReady,
    refetchInterval: 30000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    refetchOnMount: false,
    staleTime: 10000,
    cacheTime: 15 * 60 * 1000,
    retry: 1,
    onSuccess: mergeRemoteComplianceLogs,
  });

  const refreshDayBoardFromServer = async () => {
    await refetchDayBoard({ throwOnError: false });
  };
  loadAllDataRef.current = refreshDayBoardFromServer;

  useEffect(() => {
    if (summaryReady || !complianceSummaryOptions || !latestFetchedLogsRef.current) return;
    const logs = latestFetchedLogsRef.current;
    try {
      const summary = summarizeDailyCompliance(logs, complianceSummaryOptions);
      const tasks = summary?.tasks || {};
      setTaskStats(tasks);
      setCompletedTasks(
        Object.fromEntries(DAILY_TASKS.map((task) => [task.key, !!tasks[task.key]?.completed]))
      );
      setSummaryReady(true);
    } catch (_) {}
  }, [complianceSummaryOptions, summaryReady]);

  const syncTaskToDatabase = async (
    taskType,
    productId = null,
    productSku = null,
    productName = null,
    zoneName = null,
    data = {},
    reloadOptions = {}
  ) => {
    const { reloadMode = 'summary' } = reloadOptions;
    const syncKey = `${taskType}::${productId || zoneName || ''}`;
    pendingSyncsRef.current.add(syncKey);
    try {
      const productDetails = productId ? findProductById(productId) : null;
      await complianceTasksAPI.logTask({
        taskType,
        taskDate: getTodayKey(),
        productId,
        productSku,
        productName,
        zoneName,
        data,
        subtaskTotal: getSubtaskTotalForTask(taskType),
        storeProductId: productDetails?.store_product_id ?? productDetails?.storeProductId ?? productId ?? null,
        sellerId: productDetails?.seller_id ?? productDetails?.sellerId ?? null,
        productDbId: productDetails?.product_id ?? productDetails?.productId ?? null,
      });
      pendingSyncsRef.current.delete(syncKey);

      const subtaskId = taskType === 'temperatureCheck'
        ? normalizeTemperatureComplianceSubtaskId(String(productId || zoneName || ''))
        : String(productId || zoneName || '');
      const optimisticLog = {
        task_type: taskType,
        subtask_id: subtaskId,
        data: data || {},
        completed_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      const mergedCache = mergeComplianceLogsByLatestSubtask([
        ...(latestFetchedLogsRef.current || []),
        optimisticLog,
      ]);
      latestFetchedLogsRef.current = mergedCache;
      lastRemoteLogsFingerprintRef.current = buildComplianceLogsFingerprint(mergedCache);

      queryClient.setQueryData(staffDayBoardQueryKey, (previous) => ({
        ...(previous || {}),
        logs: mergedCache,
      }));

      if (reloadMode === 'full') {
        appliedDayBoardKeyRef.current = '';
        await refreshDayBoardFromServer();
      } else {
        recomputeLocalSummary(mergedCache, assignedTaskProductsRef.current);
      }
    } catch (error) {
      pendingSyncsRef.current.delete(syncKey);
      console.error('Failed to sync task to database:', error);
    }
  };

  const getTaskStat = (taskKey) => {
    const stat = taskStats[taskKey] || {};
    return {
      completedSubtasks: stat.completedSubtasks ?? 0,
      totalSubtasks: stat.totalSubtasks ?? 0,
      completed: !!stat.completed,
    };
  };

  const getTaskProductSeller = (product = {}) =>
    product.sellerName || product.seller_name || product.seller || '-';

  const getExpectedSubtasksForTask = (taskType, storeProducts, dateKey) => {
    switch (taskType) {
      case 'dateCheck':
        return getDailyDateCheckProducts().map((product) => ({
          subtask_id: product.id,
          product_name: product.name,
          product_sku: product.sku,
        }));
      case 'dailyInventory':
        return getDailyInventoryProducts().map((product) => ({
          subtask_id: product.id,
          product_name: product.name,
          product_sku: product.sku,
        }));
      case 'shortlifeDateCheck':
        return getShortlifeProducts().map((product) => ({
          subtask_id: product.id,
          product_name: product.name,
          product_sku: product.sku,
        }));
      case 'produceDateCheck':
        return getProduceProducts().map((product) => ({
          subtask_id: product.id,
          product_name: product.name,
          product_sku: product.sku,
        }));
      case 'temperatureCheck':
        return temperatureZones.map((zone) => ({
          subtask_id: zone.id,
          zone_name: zone.name,
        }));
      default:
        return [];
    }
  };

  // Get products for daily tasks - show all products in all tasks
  const getDailyDateCheckProducts = () => {
    if (assignedTaskProducts.dateCheck?.length) {
      return assignedTaskProducts.dateCheck;
    }
    return getFallbackDateCheckProducts(sortedTaskProducts);
  };

  const getDailyInventoryProducts = () => {
    if (assignedTaskProducts.dailyInventory?.length) {
      return assignedTaskProducts.dailyInventory;
    }
    return [];
  };

  const getShortlifeProducts = () => {
    return assignedTaskProducts.shortlifeDateCheck || [];
  };

  const getProduceProducts = () => {
    return assignedTaskProducts.produceDateCheck || [];
  };

  const getSubtaskTotalForTask = (taskType) => {
    switch (taskType) {
      case 'dateCheck':
        return getDailyDateCheckProducts().length;
      case 'dailyInventory':
        return getDailyInventoryProducts().length;
      case 'shortlifeDateCheck':
        return getShortlifeProducts().length;
      case 'produceDateCheck':
        return getProduceProducts().length;
      case 'temperatureCheck':
        return temperatureZones.length;
      default:
        return 1;
    }
  };

  const bumpTaskStat = (taskKey, delta) => {
    if (!delta) return;
    setTaskStats((prev) => {
      const current = prev[taskKey] || {};
      const totalSubtasks = current.totalSubtasks || getSubtaskTotalForTask(taskKey) || 0;
      const completedSubtasks = Math.max(
        0,
        Math.min(totalSubtasks, (current.completedSubtasks || 0) + delta)
      );
      const completed = totalSubtasks > 0 && completedSubtasks >= totalSubtasks;
      setCompletedTasks((prevCompleted) => ({
        ...prevCompleted,
        [taskKey]: completed,
      }));
      return {
        ...prev,
        [taskKey]: {
          ...current,
          taskType: taskKey,
          totalSubtasks,
          completedSubtasks,
          completed,
        },
      };
    });
  };
  
  // Helper to find product by ID from store products
  const findProductById = (productId) => {
    return [
      ...assignedTaskProducts.dateCheck,
      ...assignedTaskProducts.dailyInventory,
      ...assignedTaskProducts.shortlifeDateCheck,
      ...assignedTaskProducts.produceDateCheck,
      ...products,
    ].find((product) => String(product.id) === String(productId));
  };

  // Update handlers with logging
  const updateDateCheck = (productId, field, value) => {
    const product = findProductById(productId);
    if (!product) return;
    const oldData = dateCheckData[productId] || {};
    const nextData = {
      ...oldData,
      [field]: value,
    };
    
    setDateCheckData(prev => ({
      ...prev,
      [productId]: nextData
    }));

    // Sync to database
    syncTaskToDatabase('dateCheck', productId, product.sku, product.name, null, {
      date: nextData.date || '',
    });
  };

  const updateInventory = (productId, field, value) => {
    const product = findProductById(productId);
    if (!product) return;
    const oldData = normalizeInventoryCountData(inventoryData[productId] || {});
    const nextData = normalizeInventoryCountData({
      ...oldData,
      [field]: parseInventoryCountValue(value),
    });
    
    setInventoryData(prev => {
      const nextState = {
        ...prev,
        [productId]: nextData,
      };
      writeDailyInventoryDraft(dailyInventoryDraftKey, nextState);
      return nextState;
    });

    // Debounce sync so values are persisted while typing without flooding API.
    if (inventorySyncTimersRef.current[productId]) {
      clearTimeout(inventorySyncTimersRef.current[productId]);
    }
    inventorySyncTimersRef.current[productId] = setTimeout(() => {
      syncTaskToDatabase('dailyInventory', productId, product.sku, product.name, null, {
        ...nextData,
        shop_count: nextData.shopCount,
        warehouse_count: nextData.warehouseCount,
        total_quantity: nextData.totalQuantity,
        quantity: nextData.totalQuantity,
      }, { reloadMode: 'summary' });
      delete inventorySyncTimersRef.current[productId];
    }, 400);
  };

  const updateShortlife = (productId, checked) => {
    const product = findProductById(productId);
    if (!product) return;

    const wasChecked = !!(shortlifeData[productId]?.checked);
    if (checked !== wasChecked) {
      bumpTaskStat('shortlifeDateCheck', checked ? 1 : -1);
    }
    
    setShortlifeData(prev => ({
      ...prev,
      [productId]: { checked, time: new Date().toLocaleTimeString() }
    }));

    syncTaskToDatabase('shortlifeDateCheck', productId, product.sku, product.name, null, { checked });
  };

  const updateProduce = (productId, field, value) => {
    const product = findProductById(productId);
    if (!product) return;

    const oldData = produceData[productId] || {};
    const nextData = {
      ...oldData,
      time: new Date().toLocaleTimeString(),
    };

    if (field === 'notAvailable') {
      nextData.notAvailable = !!value;
      if (nextData.notAvailable) {
        nextData.status = '';
      }
    } else if (field === 'status') {
      nextData.status = value;
      nextData.notAvailable = false;
    }

    setProduceData((prev) => ({
      ...prev,
      [productId]: nextData,
    }));

    syncTaskToDatabase('produceDateCheck', productId, product.sku, product.name, null, {
      status: nextData.status || '',
      notAvailable: Boolean(nextData.notAvailable),
    });
  };

  const updateTemperature = (zoneId, temp) => {
    const zone = temperatureZones.find(z => String(z.id) === String(zoneId));
    if (!zone) return;
    const tempVal = parseFloat(temp) || 0;
    
    setTemperatureData(prev => ({
      ...prev,
      [zoneId]: { temp: tempVal, time: new Date().toLocaleTimeString() }
    }));

    // Sync to database (subtask_id = zone.id so hydration matches UI keys)
    syncTaskToDatabase('temperatureCheck', zone.id, null, null, zone.name, {
      temp: tempVal,
      assetId: zone.id,
      assetName: zone.name,
      assetType: zone.assetType || zone.asset_type || zone.type || '',
      min: zone.min,
      max: zone.max,
      location: zone.location || '',
    });
  };

  // Utility: Calculate days to expire and get color
  const getExpiryInfo = (expiryDate) => {
    if (!expiryDate) return { days: null, color: '', label: '' };
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expDate = new Date(expiryDate);
    expDate.setHours(0, 0, 0, 0);
    
    const diffTime = expDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays > 120) {
      return { days: diffDays, color: 'bg-green-100 text-green-800', label: 'Good' };
    } else if (diffDays >= 10 && diffDays <= 120) {
      return { days: diffDays, color: 'bg-yellow-100 text-yellow-800', label: 'Monitor' };
    } else if (diffDays >= 1 && diffDays < 10) {
      return { days: diffDays, color: 'bg-orange-100 text-orange-800', label: 'Critical' };
    } else {
      return { days: diffDays, color: 'bg-red-100 text-red-800', label: 'Expired' };
    }
  };

  const getPriorityBadgeClass = (priorityRank) => {
    const rank = Number(priorityRank || 0);
    if (rank === 1) return 'bg-red-100 text-red-800';
    if (rank === 2) return 'bg-orange-100 text-orange-800';
    if (rank === 3) return 'bg-blue-100 text-blue-800';
    if (rank === 5) return 'bg-gray-100 text-gray-700';
    return 'bg-slate-100 text-slate-700';
  };

  // STAFF VIEW: Date Check Table
  const DateCheckTable = () => {
    const products = getDailyDateCheckProducts();
    const { completedSubtasks, totalSubtasks } = getTaskStat('dateCheck');
    const completedCount = completedSubtasks;
    const taskAlreadyCompleted = !!completedTasks.dateCheck;
    const isDateCheckProductsLoading = isStaff
      && activeTab === 'dateCheck'
      && (
        (isDayBoardLoading && !dayBoardPayload)
        || (!assignmentsReady && products.length === 0)
      );
    
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">Date Check</h3>
            <p className="text-sm text-gray-600">
              Store: <span className="font-medium text-gray-900">{userStore.name}</span> | 
              Progress: <span className="font-medium text-blue-600">{completedCount}/{totalSubtasks}</span> products
            </p>
          </div>
          <span className="text-sm text-gray-500">{formatDate(getTodayKey())}</span>
        </div>
        
        {/* Instructions & Legend */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <p className="text-sm text-gray-600">
            Enter expiry date in format: <code className="bg-gray-100 px-2 py-1 rounded">YYYY-MM-DD</code> (e.g., 2025-12-31)
          </p>
          <div className="flex flex-wrap gap-2 text-xs">
            <span className="px-2 py-1 rounded bg-green-100 text-green-800">&gt;120 days: Good</span>
            <span className="px-2 py-1 rounded bg-yellow-100 text-yellow-800">10-120 days: Monitor</span>
            <span className="px-2 py-1 rounded bg-orange-100 text-orange-800">1-10 days: Critical</span>
            <span className="px-2 py-1 rounded bg-red-100 text-red-800">&lt;1 day: Expired</span>
          </div>
        </div>

        {taskAlreadyCompleted && (
          <div className="p-3 bg-blue-50 text-blue-800 rounded-lg text-center">
            This task is already completed for today. You can still update the data if a correction is needed.
          </div>
        )}
        
        <div className="table-scroll-shell relative min-h-[260px]">
          <DataLoadingOverlay active={isDateCheckProductsLoading} label="Loading date check products..." />
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Seller</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Priority</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expiry Date</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Days Left</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {!isDateCheckProductsLoading && products.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-sm text-gray-500">
                    No date check products are assigned for this store today.
                  </td>
                </tr>
              )}
              {products.map(product => {
                const data = dateCheckData[product.id] || {};
                const isDone = data.date;
                const expiryInfo = getExpiryInfo(data.date);
                
                return (
                  <tr key={product.id} className={isDone ? 'bg-green-50' : ''}>
                    <td className="px-4 py-3 text-sm text-gray-500 font-mono">{product.sku}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{product.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{getTaskProductSeller(product)}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 capitalize">{product.category}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex px-2 py-1 text-xs rounded-full ${getPriorityBadgeClass(product.priority_rank || product.priorityRank)}`}>
                        {product.priority_reason || product.priorityReason || 'Cycle fill'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                        <DatePicker
                          value={data.date || ''}
                          onChange={(value) => updateDateCheck(product.id, 'date', value)}
                          disabled={false}
                        />
                        {!data.date && (product.lastExpiryDate || product.last_expiry_date) && (
                          <div className="mt-1 text-[11px] text-slate-500">
                            Last expiry: {product.lastExpiryDate || product.last_expiry_date}
                          </div>
                        )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {expiryInfo.days !== null ? (
                        <span className="text-sm font-medium">{expiryInfo.days}</span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {expiryInfo.days !== null ? (
                        <span className={`inline-flex px-2 py-1 text-xs rounded-full ${expiryInfo.color}`}>
                          {expiryInfo.label}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {completedTasks.dateCheck && (
          <div className="p-3 bg-green-100 text-green-800 rounded-lg text-center">
            ✓ All products checked! Task will be marked complete.
          </div>
        )}
      </div>
    );
  };

  // STAFF VIEW: Shortlife Table
  const ShortlifeTable = () => {
    const products = getShortlifeProducts();
    const { completedSubtasks, totalSubtasks } = getTaskStat('shortlifeDateCheck');
    const completedCount = completedSubtasks;
    const taskAlreadyCompleted = !!completedTasks.shortlifeDateCheck;
    const isShortlifeProductsLoading = isStaff
      && activeTab === 'shortlifeDateCheck'
      && (
        (isDayBoardLoading && !dayBoardPayload)
        || (!assignmentsReady && products.length === 0)
      );
    
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">Short-life Check</h3>
            <p className="text-sm text-gray-600">
              Store: <span className="font-medium text-gray-900">{userStore.name}</span> | 
              Progress: <span className="font-medium text-orange-600">{completedCount}/{totalSubtasks}</span> products
            </p>
          </div>
        </div>

        {taskAlreadyCompleted && (
          <div className="p-3 bg-blue-50 text-blue-800 rounded-lg text-center">
            This task is already completed for today. You can still update the data if a correction is needed.
          </div>
        )}
        
        <div className="table-scroll-shell relative min-h-[260px]">
          <DataLoadingOverlay active={isShortlifeProductsLoading} label="Loading short-life product assignments..." />
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-orange-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Seller</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Checked</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {!isShortlifeProductsLoading && products.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-sm text-gray-500">
                    No short-life categories are mapped for this store.
                  </td>
                </tr>
              )}
              {products.map(product => {
                const data = shortlifeData[product.id] || {};
                
                return (
                  <tr key={product.id} className={data.checked ? 'bg-green-50' : ''}>
                    <td className="px-4 py-3 text-sm text-gray-500 font-mono">{product.sku}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{product.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{getTaskProductSeller(product)}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 capitalize">{product.category}</td>
                    <td className="px-4 py-3 text-center">
                      <input
                        type="checkbox"
                        checked={data.checked || false}
                        onChange={(e) => updateShortlife(product.id, e.target.checked)}
                        className="w-5 h-5 rounded border-gray-300 text-orange-500"
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {completedTasks.shortlifeDateCheck && (
          <div className="p-3 bg-green-100 text-green-800 rounded-lg text-center">
            ✓ All short-life items checked! Task will be marked complete.
          </div>
        )}
      </div>
    );
  };

  // STAFF VIEW: Produce Check Table
  const ProduceTable = () => {
    const products = getProduceProducts();
    const { completedSubtasks, totalSubtasks } = getTaskStat('produceDateCheck');
    const completedCount = completedSubtasks;
    const taskAlreadyCompleted = !!completedTasks.produceDateCheck;
    const isProduceProductsLoading = isStaff
      && activeTab === 'produceDateCheck'
      && (
        (isDayBoardLoading && !dayBoardPayload)
        || (!assignmentsReady && products.length === 0)
      );
    
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">Produce Quality Check</h3>
            <p className="text-sm text-gray-600">
              Store: <span className="font-medium text-gray-900">{userStore.name}</span> | 
              Progress: <span className="font-medium text-green-600">{completedCount}/{totalSubtasks}</span> products
            </p>
          </div>
        </div>

        {taskAlreadyCompleted && (
          <div className="p-3 bg-blue-50 text-blue-800 rounded-lg text-center">
            This task is already completed for today. You can still update the data if a correction is needed.
          </div>
        )}
        
        <div className="table-scroll-shell relative min-h-[260px]">
          <DataLoadingOverlay active={isProduceProductsLoading} label="Loading produce product assignments..." />
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-green-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Quality</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">N/A</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {!isProduceProductsLoading && products.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-sm text-gray-500">
                    No Produce categories are mapped for this store.
                  </td>
                </tr>
              )}
              {products.map(product => {
                const data = produceData[product.id] || {};
                const isDone = Boolean(data.status) || Boolean(data.notAvailable);

                return (
                  <tr key={product.id} className={isDone ? 'bg-green-50' : ''}>
                    <td className="px-4 py-3 text-sm text-gray-500 font-mono">{product.sku}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{product.name}</td>
                    <td className="px-4 py-3">
                      <div className={`flex justify-center gap-2 ${data.notAvailable ? 'opacity-50 pointer-events-none' : ''}`}>
                        {['good', 'fair', 'poor'].map((status) => (
                          <button
                            key={status}
                            type="button"
                            onClick={() => updateProduce(product.id, 'status', status)}
                            disabled={data.notAvailable}
                            className={`px-3 py-1 text-xs rounded capitalize ${
                              data.status === status 
                                ? status === 'good' ? 'bg-green-500 text-white' 
                                : status === 'fair' ? 'bg-yellow-500 text-white' 
                                : 'bg-red-500 text-white'
                                : 'bg-gray-100 hover:bg-gray-200'
                            }`}
                          >
                            {status}
                          </button>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <input
                        type="checkbox"
                        checked={data.notAvailable || false}
                        onChange={(e) => updateProduce(product.id, 'notAvailable', e.target.checked)}
                        className="w-5 h-5 rounded border-gray-300"
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {completedTasks.produceDateCheck && (
          <div className="p-3 bg-green-100 text-green-800 rounded-lg text-center">
            ✓ All produce checked! Task will be marked complete.
          </div>
        )}
      </div>
    );
  };

  // STAFF VIEW: Inventory Count Table
  const InventoryTable = () => {
    const products = getDailyInventoryProducts();
    const { completedSubtasks, totalSubtasks } = getTaskStat('dailyInventory');
    const completedCount = completedSubtasks;
    const taskAlreadyCompleted = !!completedTasks.dailyInventory;
    const isInventoryProductsLoading = isStaff
      && activeTab === 'dailyInventory'
      && (
        (isDayBoardLoading && !dayBoardPayload)
        || (!assignmentsReady && products.length === 0)
      );
    
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">Daily Inventory Count</h3>
            <p className="text-sm text-gray-600">
              Store: <span className="font-medium text-gray-900">{userStore.name}</span> | 
              Progress: <span className="font-medium text-purple-600">{completedCount}/{totalSubtasks}</span> products
            </p>
          </div>
        </div>

        {taskAlreadyCompleted && (
          <div className="p-3 bg-blue-50 text-blue-800 rounded-lg text-center">
            This task is already completed for today. You can still update the data if a correction is needed.
          </div>
        )}
        
        <div className="table-scroll-shell relative min-h-[260px]">
          <DataLoadingOverlay active={isInventoryProductsLoading} label="Loading daily inventory products..." />
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-purple-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Seller</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Shop Count</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Warehouse Count</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Total Quantity</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {!isInventoryProductsLoading && products.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-sm text-gray-500">
                    No inventory products are assigned for this store today.
                  </td>
                </tr>
              )}
              {products.map(product => {
                const rawData = inventoryData[product.id] || {};
                const data = normalizeInventoryCountData(rawData);
                const isDone = hasInventoryCountValue(rawData);
                const hasLegacyQuantity = Object.prototype.hasOwnProperty.call(rawData, 'quantity');
                const hasShopCount = Object.prototype.hasOwnProperty.call(rawData, 'shopCount') || hasLegacyQuantity;
                const hasWarehouseCount = Object.prototype.hasOwnProperty.call(rawData, 'warehouseCount');
                
                return (
                  <tr key={product.id} className={isDone ? 'bg-green-50' : ''}>
                    <td className="px-4 py-3 text-sm text-gray-500 font-mono">{product.sku}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{product.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{getTaskProductSeller(product)}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 capitalize">{product.category}</td>
                    <td className="px-4 py-3">
                      <QuantityInput
                        value={hasShopCount ? data.shopCount : undefined}
                        onChange={(value) => updateInventory(product.id, 'shopCount', value)}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <QuantityInput
                        value={hasWarehouseCount ? data.warehouseCount : undefined}
                        onChange={(value) => updateInventory(product.id, 'warehouseCount', value)}
                      />
                    </td>
                    <td className="px-4 py-3 text-center text-sm font-semibold text-gray-900">{isDone ? data.totalQuantity : '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {completedTasks.dailyInventory && (
          <div className="p-3 bg-green-100 text-green-800 rounded-lg text-center">
            ✓ All products counted! Task will be marked complete.
          </div>
        )}
      </div>
    );
  };

  // STAFF VIEW: Temperature Table
  const TemperatureTable = () => {
    const { completedSubtasks, totalSubtasks } = getTaskStat('temperatureCheck');
    const completedCount = completedSubtasks;
    const taskAlreadyCompleted = !!completedTasks.temperatureCheck;
    const isTemperatureLoading = isStaff
      && activeTab === 'temperatureCheck'
      && isDayBoardLoading
      && !dayBoardPayload;
    
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">Temperature Check</h3>
            <p className="text-sm text-gray-600">
              Store: <span className="font-medium text-gray-900">{userStore.name}</span> | 
              Progress: <span className="font-medium text-red-600">{completedCount}/{totalSubtasks}</span> zones
            </p>
          </div>
        </div>

        {taskAlreadyCompleted && (
          <div className="p-3 bg-blue-50 text-blue-800 rounded-lg text-center">
            This task is already completed for today. You can still update the data if a correction is needed.
          </div>
        )}
        
        <div className="table-scroll-shell relative min-h-[260px]">
          <DataLoadingOverlay active={isTemperatureLoading} label="Loading temperature check zones..." />
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-red-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Zone</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Range (°C)</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Reading</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {temperatureZones.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-sm text-gray-500">
                    No temperature assets are configured for this store.
                  </td>
                </tr>
              ) : temperatureZones.map(zone => {
                const data = temperatureData[zone.id] || {};
                const reading = parseTemperatureComplianceReading(data);
                const isDone = reading !== null;
                const hasRange = zone.min !== null && zone.max !== null;
                const isValid = isDone && (!hasRange || (reading >= zone.min && reading <= zone.max));
                
                return (
                  <tr key={zone.id} className={isDone ? (isValid ? 'bg-green-50' : 'bg-red-50') : ''}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{zone.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{zone.location}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {hasRange ? `${zone.min} to ${zone.max}` : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <TemperatureInput
                        value={data.temp}
                        onChange={(value) => updateTemperature(zone.id, value)}
                      />
                    </td>
                    <td className="px-4 py-3 text-center">
                      {isDone ? (
                        <span className={`inline-flex px-2 py-1 text-xs rounded-full ${isValid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {isValid ? 'OK' : 'Alert'}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {completedTasks.temperatureCheck && (
          <div className="p-3 bg-green-100 text-green-800 rounded-lg text-center">
            ✓ All zones recorded! Task will be marked complete.
          </div>
        )}
      </div>
    );
  };

  // ADMIN/MANAGER VIEW: Reports Dashboard
  const ReportsDashboard = () => {
    const effectiveReportStore = canFilterAllStores ? reportStore : currentUserStoreId;
    const visibleRows = effectiveReportStore === 'all'
      ? reportRows
      : reportRows.filter((row) => row.storeId === effectiveReportStore);

    const totalStoresActive = visibleRows.length;
    const totalCompleted = visibleRows.reduce((sum, row) => sum + (row.completedTasks || 0), 0);
    const totalAssigned = visibleRows.reduce((sum, row) => sum + (row.totalTasks || 0), 0);
    const totalFraction = visibleRows.reduce((sum, row) => sum + (row.completedTaskFraction || 0), 0);
    const taskColumns = reportTaskType === 'all'
      ? DAILY_TASKS
      : DAILY_TASKS.filter((task) => task.key === reportTaskType);
    const selectedUser = users.find((entry) => String(entry.id) === String(reportUser));
    const selectedUserStoreId = selectedUser?.store_id || selectedUser?.storeId || null;
    const reportUsers = effectiveReportStore === 'all'
      ? users
      : users.filter((entry) => String(entry.store_id || entry.storeId) === String(effectiveReportStore));
    const storeMap = new Map(stores.map((store) => [store.id, store.name]));
    const userMap = new Map(users.map((entry) => [entry.id, entry.name]));
    const selectedDates = getDateRangeValues(reportStartDate, reportEndDate);
    const reportStoreIds = effectiveReportStore === 'all'
      ? (selectedUserStoreId
          ? stores.filter((store) => String(store.id) === String(selectedUserStoreId)).map((store) => store.id)
          : stores.map((store) => store.id))
      : [effectiveReportStore];
    const reportProductsByStore = (() => {
      const map = new Map();
      reportProducts.forEach((product) => {
        const storeId = String(product.store_id || '');
        if (!map.has(storeId)) {
          map.set(storeId, []);
        }
        map.get(storeId).push(product);
      });
      return map;
    })();
    const isLogIncomplete = (log) =>
      Number(log.subtask_completed || 0) <= 0;
    const isLogOverdue = (log) =>
      !!log.task_date &&
      (log.task_date < getTodayKey() || (log.task_date === getTodayKey() && !isWithinTaskWindow()));
    const getActivityUserLabel = (log) => {
      if (isLogIncomplete(log)) {
        return '';
      }
      return log.users?.name || userMap.get(log.user_id) || '';
    };
    const getTaskLabel = (taskType) =>
      DAILY_TASKS.find((task) => task.key === taskType)?.label || taskType;
    const getLogData = (log) => (
      log?.data && typeof log.data === 'object' && !Array.isArray(log.data) ? log.data : {}
    );
    const toDisplayNumber = (value) => {
      if (value === null || value === undefined || value === '') return '-';
      const numeric = Number(value);
      return Number.isFinite(numeric) ? numeric : '-';
    };
    const getTaskEnteredValueColumns = (taskType) => {
      switch (taskType) {
        case 'dateCheck':
          return [
            {
              key: 'expiryDate',
              label: 'Expiry Date',
              getValue: (log) => {
                const data = getLogData(log);
                const hasNotAvailable = Object.prototype.hasOwnProperty.call(data, 'notAvailable');
                const isNotAvailable = Boolean(data?.notAvailable);
                if (data?.date) return data.date;
                if (hasNotAvailable && isNotAvailable) return 'N/A';
                return '-';
              },
            },
            {
              key: 'daysLeft',
              label: 'Days Left',
              getValue: (log) => {
                const data = getLogData(log);
                const hasNotAvailable = Object.prototype.hasOwnProperty.call(data, 'notAvailable');
                const isNotAvailable = Boolean(data?.notAvailable);
                if (hasNotAvailable && isNotAvailable) return 'N/A';
                const expiryInfo = getExpiryInfo(data?.date);
                return expiryInfo.days ?? '-';
              },
            },
            {
              key: 'expiryStatus',
              label: 'Status',
              getValue: (log) => {
                const data = getLogData(log);
                const hasNotAvailable = Object.prototype.hasOwnProperty.call(data, 'notAvailable');
                const isNotAvailable = Boolean(data?.notAvailable);
                if (hasNotAvailable && isNotAvailable) return 'N/A';
                const expiryInfo = getExpiryInfo(data?.date);
                return expiryInfo.label || '-';
              },
            },
            {
              key: 'notAvailable',
              label: 'N/A',
              getValue: (log) => {
                const data = getLogData(log);
                const hasNotAvailable = Object.prototype.hasOwnProperty.call(data, 'notAvailable');
                if (!hasNotAvailable) return '-';
                return data?.notAvailable ? 'Yes' : 'No';
              },
            },
          ];
        case 'dailyInventory':
          return [
            {
              key: 'shopCount',
              label: 'Shop Count',
              getValue: (log) => {
                const data = getLogData(log);
                return toDisplayNumber(data?.shopCount ?? data?.shop_count);
              },
            },
            {
              key: 'warehouseCount',
              label: 'Warehouse Count',
              getValue: (log) => {
                const data = getLogData(log);
                return toDisplayNumber(data?.warehouseCount ?? data?.warehouse_count);
              },
            },
            {
              key: 'totalQuantity',
              label: 'Total Quantity',
              getValue: (log) => {
                const data = getLogData(log);
                const explicitTotal = data?.totalQuantity ?? data?.total_quantity;
                if (explicitTotal !== null && explicitTotal !== undefined && explicitTotal !== '') {
                  return toDisplayNumber(explicitTotal);
                }
                const shopCount = Number(data?.shopCount ?? data?.shop_count);
                const warehouseCount = Number(data?.warehouseCount ?? data?.warehouse_count);
                if (Number.isFinite(shopCount) || Number.isFinite(warehouseCount)) {
                  return (Number.isFinite(shopCount) ? shopCount : 0) + (Number.isFinite(warehouseCount) ? warehouseCount : 0);
                }
                return '-';
              },
            },
          ];
        case 'shortlifeDateCheck':
          return [{
            key: 'checked',
            label: 'Checked',
            getValue: (log) => {
              const data = getLogData(log);
              const hasChecked = Object.prototype.hasOwnProperty.call(data, 'checked');
              if (!hasChecked) {
                return Number(log?.subtask_completed || 0) > 0 ? 'Yes' : '-';
              }
              return data?.checked ? 'Yes' : 'No';
            },
          }];
        case 'produceDateCheck':
          return [
            {
              key: 'quality',
              label: 'Quality',
              getValue: (log) => {
                const data = getLogData(log);
                if (data?.notAvailable) return 'N/A';
                const value = String(data?.status || '').trim();
                return value ? `${value.charAt(0).toUpperCase()}${value.slice(1)}` : '-';
              },
            },
            {
              key: 'notAvailable',
              label: 'N/A',
              getValue: (log) => {
                const data = getLogData(log);
                const hasNotAvailable = Object.prototype.hasOwnProperty.call(data, 'notAvailable');
                if (!hasNotAvailable) return '-';
                return data?.notAvailable ? 'Yes' : 'No';
              },
            },
          ];
        case 'temperatureCheck':
          return [
            {
              key: 'reading',
              label: 'Reading',
              getValue: (log) => {
                const data = getLogData(log);
                const temp = parseTemperatureComplianceReading(data);
                return temp !== null ? `${temp}\u00b0C` : '-';
              },
            },
            {
              key: 'temperatureStatus',
              label: 'Status',
              getValue: (log) => {
                const data = getLogData(log);
                const temp = parseTemperatureComplianceReading(data);
                if (temp === null) return '-';
                const zoneId = normalizeTemperatureComplianceSubtaskId(
                  String(log?.subtask_id || log?.zone_name || '')
                );
                const zone = temperatureZones.find((entry) => String(entry.id) === zoneId);
                const min = Number(zone?.min ?? data.min ?? data.temperatureMin ?? data.temperature_min);
                const max = Number(zone?.max ?? data.max ?? data.temperatureMax ?? data.temperature_max);
                if (!Number.isFinite(min) || !Number.isFinite(max)) return 'Recorded';
                return temp >= min && temp <= max ? 'OK' : 'Alert';
              },
            },
          ];
        default:
          return [];
      }
    };
    const activityEnteredValueColumns = reportTaskType === 'all'
      ? []
      : getTaskEnteredValueColumns(reportTaskType);
    const activityColumnCount = 7 + activityEnteredValueColumns.length;
    const activityRows = (() => {
      const actualLogs = reportLogs.filter((log) => {
        if (reportStore !== 'all' && String(log.store_id) !== String(reportStore)) return false;
        if (reportUser !== 'all' && String(log.user_id) !== String(reportUser)) return false;
        if (reportTaskType !== 'all' && log.task_type !== reportTaskType) return false;
        return true;
      });

      const logKeySet = new Set(
        actualLogs.map((log) => `${log.task_date}::${log.store_id}::${log.task_type}::${String(log.subtask_id || '')}`)
      );

      const syntheticLogs = [];
      reportStoreIds.forEach((storeId) => {
        const storeProducts = reportProductsByStore.get(String(storeId)) || [];
        selectedDates.forEach((dateKey) => {
          const taskTypes = reportTaskType === 'all'
            ? DAILY_TASKS.map((task) => task.key)
            : [reportTaskType];

          taskTypes.forEach((taskType) => {
            const expectedSubtasks = getExpectedSubtasksForTask(taskType, storeProducts, dateKey);
            expectedSubtasks.forEach((subtask) => {
              const syntheticKey = `${dateKey}::${storeId}::${taskType}::${String(subtask.subtask_id || '')}`;
              if (!logKeySet.has(syntheticKey)) {
                syntheticLogs.push({
                  id: `synthetic-${syntheticKey}`,
                  task_date: dateKey,
                  store_id: storeId,
                  user_id: null,
                  task_type: taskType,
                  subtask_id: subtask.subtask_id,
                  product_name: subtask.product_name || null,
                  product_sku: subtask.product_sku || null,
                  zone_name: subtask.zone_name || null,
                  subtask_completed: 0,
                  subtask_total: 1,
                  completed_at: null,
                  synthetic: true,
                });
              }
            });
          });
        });
      });

      const combinedRows = [...actualLogs, ...syntheticLogs].sort((a, b) => {
        const dateCompare = String(b.task_date || '').localeCompare(String(a.task_date || ''));
        if (dateCompare !== 0) return dateCompare;
        return String(a.task_type || '').localeCompare(String(b.task_type || ''));
      });

      return combinedRows.filter((log) => {
        if (activityStatusFilter === 'completed') {
          return Number(log.subtask_completed || 0) > 0;
        }
        if (activityStatusFilter === 'incomplete') {
          return isLogIncomplete(log);
        }
        return true;
      });
    })();

    const exportActivityReport = () => {
      if (!activityRows.length) return;

      const exportableTasks = DAILY_TASKS.filter((task) => reportTaskType === 'all' || task.key === reportTaskType);
      const sheets = exportableTasks.map((task) => {
        const enteredValueColumns = reportTaskType === 'all'
          ? []
          : getTaskEnteredValueColumns(task.key);
        const columns = [
          { header: 'Date', key: 'Date', width: 14 },
          { header: 'Store', key: 'Store', width: 20 },
          { header: 'User', key: 'User', width: 20 },
          { header: 'Task', key: 'Task', width: 18 },
          { header: 'Item', key: 'Item', width: 24 },
          ...enteredValueColumns.map((column) => ({ header: column.label, key: column.label, width: 18 })),
          { header: 'Completed', key: 'Completed', width: 12 },
          { header: 'Updated', key: 'Updated', width: 22 },
        ];
        const rows = activityRows
          .filter((log) => log.task_type === task.key)
          .map((log) => {
            const row = {
              Date: normalizeDateKeyValue(log.task_date) || '-',
              Store: storeMap.get(log.store_id) || log.store_id || '-',
              User: getActivityUserLabel(log),
              Task: getTaskLabel(log.task_type),
              Item: log.product_name || log.zone_name || log.subtask_id || '-',
              Completed: Number(log.subtask_completed || 0) > 0 ? 'Completed' : 'Pending',
              Updated: log.completed_at ? new Date(log.completed_at).toLocaleString() : '-',
            };

            enteredValueColumns.forEach((column) => {
              row[column.label] = column.getValue(log);
            });

            return row;
          });

        return {
          sheetName: task.label.slice(0, 31),
          columns,
          rows: rows.length ? rows : [{
            Date: '-',
            Store: '-',
            User: '-',
            Task: task.label,
            Item: '-',
            Completed: '-',
            Updated: '-',
          }],
        };
      });

      downloadExcelWorkbook({
        fileName: `task-report-activity-${reportStartDate}-to-${reportEndDate}.xlsx`,
        sheets,
      });
    };
    reportExportHandlerRef.current = exportActivityReport;

    return (
      <div className="space-y-4">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          <div className="card bg-blue-50">
            <div className="flex items-center gap-3">
              <Store className="h-8 w-8 text-blue-500" />
              <div>
                <p className="text-sm text-gray-600">Stores Active</p>
                <p className="text-2xl font-bold">{totalStoresActive}</p>
              </div>
            </div>
          </div>
          <div className="card bg-indigo-50">
            <div className="flex items-center gap-3">
              <ClipboardCheck className="h-8 w-8 text-indigo-500" />
              <div>
                <p className="text-sm text-gray-600">Total Assigned Tasks</p>
                <p className="text-2xl font-bold">{totalAssigned}</p>
              </div>
            </div>
          </div>
          <div className="card bg-green-50">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-8 w-8 text-green-500" />
              <div>
                <p className="text-sm text-gray-600">Tasks Completed</p>
                <p className="text-2xl font-bold">{totalCompleted}</p>
              </div>
            </div>
          </div>
          <div className="card bg-orange-50">
            <div className="flex items-center gap-3">
              <TrendingUp className="h-8 w-8 text-orange-500" />
              <div>
                <p className="text-sm text-gray-600">Avg Completion</p>
                <p className="text-2xl font-bold">
                  {totalAssigned > 0 ? Math.round((totalFraction / totalAssigned) * 100) : 0}%
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="card flex flex-wrap gap-4">
          <div>
            <label className="label">Start Date</label>
            <input
              type="date"
              value={reportStartDate}
              onChange={(e) => setReportStartDate(e.target.value)}
              min={isSupervisorUser ? supervisorReportMinDate : undefined}
              max={isSupervisorUser ? todayKey : undefined}
              className="input"
            />
          </div>
          <div>
            <label className="label">End Date</label>
            <input
              type="date"
              value={reportEndDate}
              onChange={(e) => setReportEndDate(e.target.value)}
              min={isSupervisorUser ? supervisorReportMinDate : undefined}
              max={isSupervisorUser ? todayKey : undefined}
              className="input"
            />
          </div>
          <div>
            <FilterSelect
              label="User"
              value={reportUser}
              onChange={setReportUser}
              className="min-w-[220px]"
              options={[
                { value: 'all', label: 'All Users' },
                ...reportUsers.map((entry) => ({ value: entry.id, label: entry.name })),
              ]}
            />
          </div>
          <div>
            <FilterSelect
              label="Task Type"
              value={reportTaskType}
              onChange={setReportTaskType}
              className="min-w-[220px]"
              options={[
                { value: 'all', label: 'All Task Types' },
                ...DAILY_TASKS.map((task) => ({ value: task.key, label: task.label })),
              ]}
            />
          </div>
        </div>

        {/* Detailed Table */}
        <div className="card overflow-hidden p-0">
          <h3 className="text-lg font-semibold p-4 border-b">
            Store Task Completion - {formatDate(reportStartDate)} to {formatDate(reportEndDate)}
          </h3>
          <div className="table-scroll-shell">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Store</th>
                  {taskColumns.map((task) => (
                    <th key={task.key} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      {task.label}
                    </th>
                  ))}
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Progress</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Updated</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {visibleRows.map((data) => (
                  <tr key={data.storeId} className="align-top">
                    <td className="px-4 py-4 text-sm font-medium text-gray-900">{data.storeName}</td>
                    {taskColumns.map(task => (
                      <td key={task.key} className="px-4 py-4 text-center">
                        <div className="flex flex-col items-center justify-center gap-1 min-h-[56px]">
                          {(() => {
                            const entry = data.tasks?.find((item) => item.taskType === task.key);
                            if (entry?.completed) {
                              return <CheckCircle2 className="h-5 w-5 text-green-500" />;
                            }
                            if ((entry?.completedDays || 0) > 0) {
                              return <CheckCircle2 className="h-5 w-5 text-orange-500" />;
                            }
                            return <Circle className="h-5 w-5 text-gray-300" />;
                          })()}
                          <span className="text-xs text-gray-500">
                            {data.tasks?.find((entry) => entry.taskType === task.key)?.completedDays || 0}/
                            {data.tasks?.find((entry) => entry.taskType === task.key)?.totalDays || 0}
                          </span>
                        </div>
                      </td>
                    ))}
                    <td className="px-4 py-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-16 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-primary-600 h-2 rounded-full"
                            style={{ width: `${data.progress || 0}%` }}
                          />
                        </div>
                        <span className="text-xs">{data.progress || 0}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500">
                      <div className="min-h-[56px] flex flex-col justify-center">
                        <span>{data.lastUpdatedBy || '-'}</span>
                        <span className="text-xs text-gray-400">
                          {data.lastUpdated ? new Date(data.lastUpdated).toLocaleString() : ''}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
                {visibleRows.length === 0 && (
                  <tr>
                    <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                      No data available for the selected filters
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="card overflow-hidden p-0">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Activity Log</h3>
            </div>
          </div>
          <div className="px-4 py-3 border-b bg-gray-50 flex flex-wrap gap-4 text-sm text-gray-600">
            <span className="inline-flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              {reportStartDate} to {reportEndDate}
            </span>
            <span className="inline-flex items-center gap-1">
              <Store className="h-4 w-4" />
              {storeMap.get(reportStore) || currentStoreName || reportStore}
            </span>
            <span className="inline-flex items-center gap-1">
              <User className="h-4 w-4" />
              {reportUser === 'all' ? 'All Users' : userMap.get(reportUser) || reportUser}
            </span>
            <span className="inline-flex items-center gap-1">
              <Filter className="h-4 w-4" />
              {reportTaskType === 'all'
                ? 'All Task Types'
                : DAILY_TASKS.find((task) => task.key === reportTaskType)?.label || reportTaskType}
            </span>
            <span className="inline-flex items-center gap-1">
              <Filter className="h-4 w-4" />
              {activityRows.length} records
            </span>
            <div className="min-w-[180px]">
              <FilterSelect
                value={activityStatusFilter}
                onChange={setActivityStatusFilter}
                options={[
                  { value: 'all', label: 'All Status' },
                  { value: 'completed', label: 'Completed' },
                  { value: 'incomplete', label: 'Incomplete' },
                ]}
              />
            </div>
          </div>
          <div className="table-scroll-shell">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Store</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Task</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Item</th>
                  {activityEnteredValueColumns.map((column) => (
                    <th key={column.key} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {column.label}
                    </th>
                  ))}
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Completed</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Updated</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {activityRows.length === 0 ? (
                  <tr>
                    <td colSpan={activityColumnCount} className="px-4 py-8 text-center text-gray-500">
                      No activity recorded for the selected filters.
                    </td>
                  </tr>
                ) : (
                  activityRows.map((log) => (
                    <tr key={log.id}>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                        {normalizeDateKeyValue(log.task_date) || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">{storeMap.get(log.store_id) || log.store_id || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{getActivityUserLabel(log)}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{getTaskLabel(log.task_type)}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{log.product_name || log.zone_name || log.subtask_id || '-'}</td>
                      {activityEnteredValueColumns.map((column) => (
                        <td key={`${log.id}-${column.key}`} className="px-4 py-3 text-sm text-gray-700">
                          {column.getValue(log)}
                        </td>
                      ))}
                      <td className="px-4 py-3 text-sm text-center text-gray-700">
                        {Number(log.subtask_completed || 0) > 0 ? (
                          <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                        ) : isLogOverdue(log) ? (
                          <XCircle className="h-5 w-5 text-red-500 mx-auto" />
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {log.completed_at ? new Date(log.completed_at).toLocaleString() : '-'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (isStaff || stores.length === 0 || !reportProductsReady) return;

    const loadReportRows = async () => {
      try {
        const response = await complianceTasksAPI.getLogs({
          dateFrom: reportStartDate,
          dateTo: reportEndDate,
          ...((canFilterAllStores ? reportStore : currentUserStoreId) !== 'all'
            ? { storeId: canFilterAllStores ? reportStore : currentUserStoreId }
            : {}),
          ...(reportUser !== 'all' ? { userId: reportUser } : {}),
          ...(reportTaskType !== 'all' ? { taskType: reportTaskType } : {})
        });
        const logs = response.data?.data || [];
        setReportLogs(logs);
        const selectedDates = getDateRangeValues(reportStartDate, reportEndDate);
        const selectedUser = users.find((entry) => String(entry.id) === String(reportUser));
        const selectedUserStoreId = selectedUser?.store_id || selectedUser?.storeId || null;
        const effectiveStoreId = canFilterAllStores ? reportStore : currentUserStoreId;
        const storesToShow = effectiveStoreId === 'all'
          ? (selectedUserStoreId
              ? stores.filter((store) => String(store.id) === String(selectedUserStoreId))
              : stores)
          : stores.filter((store) => String(store.id) === String(effectiveStoreId));
        const taskKeysToShow = reportTaskType === 'all'
          ? DAILY_TASKS.map((task) => task.key)
          : [reportTaskType];
        const buildReportSummaryOptionsForStore = (storeId, storeLogsWithDateKey) => {
          const isCurrentStaffStore = String(storeId) === String(currentUserStoreId);
          const scopedCatalog = isCurrentStaffStore && sortedTaskProducts.length
            ? sortedTaskProducts
            : sortTaskProductsDeterministically(
                (reportProducts || []).filter((product) => {
                  const pid = String(product.store_id ?? product.storeId ?? '').trim();
                  return !pid || pid === String(storeId);
                })
              );
          const shortlifeIds = scopedCatalog
            .filter((product) => {
              if (isExplicitlyUnpublished(product)) return false;
              const parentCategory = String(
                product.ParentCategory || product.parent_category || product.parent_category_id || ''
              ).toLowerCase();
              const category = String(
                product.Category || product.category || product.category_id || ''
              ).toLowerCase();
              return parentCategory.includes('egg and dairy') || category.includes('bread');
            })
            .slice(0, 20)
            .map((p) => String(p.id));
          const produceIdsFromCatalog = scopedCatalog
            .filter((product) => (
              isProduceCatalogPublished(product)
              && isFruitsAndVegetablesParentCategory(product)
              && isProduceCatalogStoreScoped(product, storeId)
            ))
            .map((p) => String(p.id));
          const produceIdsFromStoreTaskList = isCurrentStaffStore
            ? uniqSubtaskIdsForSummary((produceProducts || []).map((p) => String(p.id)))
            : [];
          const produceIdsFromLogs = uniqSubtaskIdsForSummary(
            (storeLogsWithDateKey || [])
              .filter((log) => log.task_type === 'produceDateCheck')
              .map((log) => String(log.subtask_id || ''))
          );
          const temperatureIdsFromStoreTaskList = isCurrentStaffStore
            ? uniqSubtaskIdsForSummary((temperatureZones || []).map((zone) => String(zone.id)))
            : [];
          const temperatureIdsFromLogs = uniqSubtaskIdsForSummary(
            (storeLogsWithDateKey || [])
              .filter((log) => log.task_type === 'temperatureCheck')
              .map((log) => String(log.subtask_id || ''))
          );
          const dailyInventoryIdsFromLogs = uniqSubtaskIdsForSummary(
            (storeLogsWithDateKey || [])
              .filter((log) => log.task_type === 'dailyInventory')
              .map((log) => String(log.subtask_id || ''))
          );
          return {
            dateCheck: uniqSubtaskIdsForSummary(buildTaskWindow(scopedCatalog, 0, 30).map((p) => String(p.id))),
            dailyInventory: dailyInventoryIdsFromLogs,
            shortlifeDateCheck: uniqSubtaskIdsForSummary(shortlifeIds),
            produceDateCheck: uniqSubtaskIdsForSummary(
              produceIdsFromStoreTaskList.length
                ? produceIdsFromStoreTaskList
                : (produceIdsFromCatalog.length ? produceIdsFromCatalog : produceIdsFromLogs)
            ),
            temperatureCheck: temperatureIdsFromStoreTaskList.length
              ? temperatureIdsFromStoreTaskList
              : temperatureIdsFromLogs,
          };
        };

        const summaries = storesToShow.map((store) => {
          const storeLogs = logs.filter((log) => String(log.store_id) === String(store.id));
          const storeLogsWithDateKey = storeLogs.map((log) => ({
            ...log,
            __dateKey: normalizeDateKeyValue(log.task_date),
          }));
          const expectedSubtaskIdsByTask = buildReportSummaryOptionsForStore(store.id, storeLogsWithDateKey);
          const tasksByType = DAILY_TASKS.filter((task) => taskKeysToShow.includes(task.key)).map((task) => {
            const logsByDate = selectedDates.flatMap((dateValue) =>
              storeLogsWithDateKey.filter((log) => log.task_type === task.key && log.__dateKey === dateValue)
            );
            const expectedIds = expectedSubtaskIdsByTask[task.key] || [];
            const dailySummaries = selectedDates.map((dateValue) => {
              const dayLogs = storeLogsWithDateKey.filter(
                (log) => log.task_type === task.key && log.__dateKey === dateValue
              );
              const hasCompletionMarker = dayLogs.some(
                (log) => String(log.subtask_id || '') === '__completion__'
              );
              const itemLogs = dayLogs.filter(
                (log) => String(log.subtask_id || '') !== '__completion__'
              );
              const doneIds = new Set(
                itemLogs
                  .filter((log) => Number(log.subtask_completed ?? 1) > 0)
                  .map((log) => String(log.subtask_id || ''))
                  .filter(Boolean)
              );
              // The denominator is FROZEN per day: past days are judged
              // against the expected total the app recorded on that day's
              // logs (subtask_total), never against today's catalog. Only
              // the current, still-in-progress day uses the live list.
              const frozenTotal = itemLogs.reduce(
                (max, log) => Math.max(max, Number(log.subtask_total) || 0),
                0
              );
              const totalSubtasks = dateValue === todayKey
                ? (expectedIds.length || frozenTotal)
                : (frozenTotal || expectedIds.length);
              const completed = hasCompletionMarker
                || (totalSubtasks > 0 && doneIds.size >= totalSubtasks);
              const fraction = completed
                ? 1
                : (totalSubtasks > 0 ? Math.min(1, doneIds.size / totalSubtasks) : 0);
              const lastUpdated = dayLogs
                .map((log) => log.completed_at || log.updated_at)
                .filter(Boolean)
                .sort()
                .pop() || null;
              return { completed, fraction, lastUpdated };
            });
            const completedCount = dailySummaries.filter((entry) => entry?.completed).length;
            const fractionSum = dailySummaries.reduce((sum, entry) => sum + (entry?.fraction || 0), 0);
            const lastUpdated = dailySummaries
              .map((entry) => entry?.lastUpdated)
              .filter(Boolean)
              .sort()
              .pop() || null;

            return {
              taskType: task.key,
              completed: completedCount === selectedDates.length && selectedDates.length > 0,
              completionPercentage: selectedDates.length > 0
                ? Math.round((fractionSum / selectedDates.length) * 100)
                : 0,
              completedDays: completedCount,
              totalDays: selectedDates.length,
              fractionSum,
              logCount: logsByDate.length,
              lastUpdated
            };
          });

          const completedTasks = tasksByType.reduce((sum, task) => sum + task.completedDays, 0);
          const completedTaskFraction = tasksByType.reduce((sum, task) => sum + (task.fractionSum || 0), 0);
          const totalTasks = taskKeysToShow.length * selectedDates.length;
          const lastUpdated = tasksByType
            .map((task) => task.lastUpdated)
            .filter(Boolean)
            .sort()
            .pop() || null;

          return {
            storeId: store.id,
            storeName: store.name,
            completedTasks,
            completedTaskFraction,
            totalTasks,
            progress: totalTasks > 0 ? Math.round((completedTaskFraction / totalTasks) * 100) : 0,
            tasks: tasksByType,
            lastUpdated,
            lastUpdatedBy: null
          };
        });

        setReportRows(summaries);
      } catch (error) {
        console.error('Failed to load report rows:', error);
        setReportLogs([]);
      }
    };

    loadReportRows();
  }, [
    canFilterAllStores,
    currentUserStoreId,
    isStaff,
    produceProducts,
    reportEndDate,
    reportProducts,
    reportProductsReady,
    reportStartDate,
    reportStore,
    reportTaskType,
    reportUser,
    sortedTaskProducts,
    stores,
    temperatureZones,
    todayKey,
    users,
  ]);

  // STAFF DASHBOARD VIEW
  const StaffDashboard = () => {
    const assignedTaskCountToday = DAILY_TASKS.length;
    const tasksFullyDone = summaryReady ? Object.values(completedTasks).filter(Boolean).length : 0;
    const taskCompletionPercent = assignedTaskCountToday > 0
      ? Math.round((tasksFullyDone / assignedTaskCountToday) * 100)
      : 0;

    const renderActiveTable = () => {
      switch (activeTab) {
        case 'dateCheck': return DateCheckTable();
        case 'shortlifeDateCheck': return ShortlifeTable();
        case 'produceDateCheck': return ProduceTable();
        case 'dailyInventory': return InventoryTable();
        case 'temperatureCheck': return TemperatureTable();
        default: return DateCheckTable();
      }
    };

    return (
      <div className="space-y-6">
        {/* Progress Overview */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold">Today's Progress</h2>
              <p className="text-sm text-gray-600">
                Store: <span className="font-medium text-primary-600">{userStore.name}</span> | 
                Complete all 5 compliance tasks
              </p>
            </div>
            <div className="text-right">
              <span className="text-3xl font-bold text-primary-600">{taskCompletionPercent}%</span>
              <p className="text-sm text-gray-500">
                {summaryReady
                  ? `${tasksFullyDone}/${assignedTaskCountToday} tasks fully complete`
                  : (isDayBoardLoading && !dayBoardPayload ? 'Loading progress...' : 'Updating...')}
              </p>
              {isDayBoardFetching && dayBoardPayload && (
                <p className="text-xs text-gray-400">Refreshing data...</p>
              )}
              <p className="text-xs text-gray-400 mt-1 max-w-xs ml-auto">
                Subtask progress remains in each task table for detail.
              </p>
            </div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div 
              className="bg-primary-600 h-3 rounded-full transition-all duration-500"
              style={{ width: `${taskCompletionPercent}%` }}
            />
          </div>
        </div>

        {/* Top Navigation */}
        <div className="flex flex-wrap gap-2">
          {DAILY_TASKS.map(task => {
            const Icon = task.icon;
            const isCompleted = completedTasks[task.key];
            const isActive = activeTab === task.key;
            
            return (
              <button
                key={task.key}
                onClick={() => setActiveTab(task.key)}
                className={`flex items-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                  isActive 
                    ? `border-primary-500 bg-primary-50` 
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                }`}
              >
                <div className={`p-1.5 rounded ${task.color}`}>
                  <Icon className="h-4 w-4 text-white" />
                </div>
                <span className={`font-medium ${isActive ? 'text-primary-700' : 'text-gray-700'}`}>
                  {task.label}
                </span>
                {isCompleted && (
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                )}
              </button>
            );
          })}
        </div>

        {/* Active Task Table */}
        <div className="card">
          {renderActiveTable()}
        </div>
      </div>
    );
  };

  // Main render
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isStaff ? 'Daily Compliance Tasks' : 'Task Reports'}
          </h1>
          <p className="text-gray-600">
            {isStaff 
              ? `Complete your daily compliance checklist for ${userStore.name}` 
              : 'Monitor staff task completion across stores'}
          </p>
        </div>
        {!isStaff && (
          <div className="flex items-center gap-3">
            <ExportButton onClick={() => reportExportHandlerRef.current?.()} />
          </div>
        )}
      </div>

      {/* Render appropriate view */}
      {isStaff ? StaffDashboard() : <ReportsDashboard />}
    </div>
  );
};

export default DailyComplianceTasks;
